<template>
    <div class="track-editor">
        <!-- Toolbar -->
        <div class="track-editor-toolbar bg-grey-1">
            <div class="row items-center q-gutter-sm q-pa-sm">
                <q-btn flat round dense :title="ts('addMark')" @click="addObject(CourseObjectType.Mark)">
                    <icon-mark />
                    <q-tooltip>{{ ts('addMark') }}</q-tooltip>
                </q-btn>
                <q-btn flat round dense :title="ts('addGate')" @click="addObject(CourseObjectType.Gate)">
                    <icon-gate />
                    <q-tooltip>{{ ts('addGate') }}</q-tooltip>
                </q-btn>
                <q-btn flat round dense :title="ts('addRaceLine')" @click="addObject(CourseObjectType.RaceLine)">
                    <icon-race-line />
                    <q-tooltip>{{ ts('addRaceLine') }}</q-tooltip>
                </q-btn>
                <q-btn flat round dense :title="ts('addBoat')" @click="addObject(CourseObjectType.Boat)">
                    <icon-boat />
                    <q-tooltip>{{ ts('addBoat') }}</q-tooltip>
                </q-btn>
                <q-btn flat round dense :title="ts('addObstacle')" @click="addObject(CourseObjectType.Obstacle)">
                    <icon-obstacle />
                    <q-tooltip>{{ ts('addObstacle') }}</q-tooltip>
                </q-btn>
                <q-btn flat round dense :title="ts('addText')" @click="addObject(CourseObjectType.Text)">
                    <icon-text />
                    <q-tooltip>{{ ts('addText') }}</q-tooltip>
                </q-btn>
                <q-separator vertical inset />
                <q-btn flat round dense color="primary" :title="ts('copyToClipboard')" @click="copyToClipboard()">
                    <q-icon name="content_copy" />
                    <q-tooltip>{{ ts('copyToClipboard') }}</q-tooltip>
                </q-btn>
                <q-btn flat round dense color="primary" :title="ts('exportImage')" @click="exportAsImage()">
                    <q-icon name="download" />
                    <q-tooltip>{{ ts('exportImage') }}</q-tooltip>
                </q-btn>
                <q-separator vertical inset />
                <q-btn flat round dense :title="ts('zoomOut')" :disabled="zoom <= 0.25" @click="zoomOut()">
                    <q-icon name="zoom_out" />
                    <q-tooltip>{{ ts('zoomOut') }}</q-tooltip>
                </q-btn>
                <q-btn flat round dense :title="ts('zoomIn')" :disabled="zoom >= 2.0" @click="zoomIn()">
                    <q-icon name="zoom_in" />
                    <q-tooltip>{{ ts('zoomIn') }}</q-tooltip>
                </q-btn>
                <q-btn flat round dense :title="ts('zoomReset')" @click="resetZoom()">
                    <q-icon name="zoom_in" />
                    <div class="absolute-center text-bold" style="font-size: 8px; margin-top: 10px">1:1</div>
                    <q-tooltip>{{ ts('zoomReset') }}</q-tooltip>
                </q-btn>
                <q-separator vertical inset />
                <q-btn flat round dense color="negative" :title="ts('removeAllObjects')" @click="removeAllObjects()">
                    <q-icon name="delete_sweep" />
                    <q-tooltip>{{ ts('removeAllObjects') }}</q-tooltip>
                </q-btn>
            </div>
        </div>

        <div class="track-editor-canvas-container col overflow-auto" @wheel="handleWheel">
            <canvas
                ref="canvasRef"
                class="track-editor-canvas shadow-1"
                :style="{ cursor: isDragging ? 'grabbing' : 'default', width: canvasWidth + 'px', height: canvasHeight + 'px' }"
                @mousedown="handleMouseDown"
                @mousemove="handleMouseMove"
                @mouseup="handleMouseUp"
                @mouseleave="handleMouseUp"
                @dblclick="handleDoubleClick"
                @contextmenu.prevent="handleContextMenu"
            ></canvas>

            <q-menu
                v-model="contextMenuVisible"
                touch-position
                context-menu
            >
                <q-list style="min-width: 100px" v-if="contextMenuObject">
                    <!-- Boat Color Section (for RaceLine only, now at the beginning) -->
                    <q-item v-if="contextMenuObject?.type === CourseObjectType.RaceLine">
                        <q-item-section avatar>
                            <q-icon name="directions_boat" />
                        </q-item-section>
                        <q-item-section>
                            <div class="row q-gutter-xs q-mt-xs">
                                <div
                                    v-for="color in Colors"
                                    :key="color.name"
                                    class="color-picker-box cursor-pointer"
                                    :style="{ backgroundColor: getColorValue(color.name, CourseObjectType.Boat) }"
                                    @click="changeColor(contextMenuObject!, color.name, 'third')"
                                >
                                    <q-tooltip>{{ ts(color.name) }}</q-tooltip>
                                </div>
                            </div>
                        </q-item-section>
                    </q-item>
                    <q-separator v-if="contextMenuObject?.type === CourseObjectType.RaceLine" />

                    <!-- Flag Colors Section (for RaceLine) -->
                    <q-item v-if="contextMenuObject?.type === CourseObjectType.RaceLine">
                        <q-item-section avatar>
                            <div class="relative-position">
                                <q-icon name="flag" color="grey-7" />
                                <div
                                    class="absolute-center text-white text-bold"
                                    style="font-size: 10px; margin-top: 1px"
                                >1</div>
                            </div>
                        </q-item-section>
                        <q-item-section>
                            <q-item-label caption>{{ ts('firstFlagRC') }}</q-item-label>
                            <div class="row q-gutter-xs q-mt-xs">
                                <div
                                    v-for="color in FlagColors"
                                    :key="color.name"
                                    class="color-picker-box cursor-pointer"
                                    :class="{ 'selected': contextMenuObject.colorFlag1 === color.name }"
                                    :style="{ backgroundColor: color.value, border: color.name === 'white' ? '1px solid #ccc' : 'none' }"
                                    @click="changeFlagColor(contextMenuObject!, color.name, 1)"
                                >
                                    <q-tooltip>{{ ts(color.name) }}</q-tooltip>
                                </div>
                            </div>
                        </q-item-section>
                    </q-item>
                    <q-item v-if="contextMenuObject?.type === CourseObjectType.RaceLine">
                        <q-item-section avatar>
                            <div class="relative-position">
                                <q-icon name="flag" color="grey-7" />
                                <div
                                    class="absolute-center text-white text-bold"
                                    style="font-size: 10px; margin-top: 1px"
                                >2</div>
                            </div>
                        </q-item-section>
                        <q-item-section>
                            <q-item-label caption>{{ ts('secondFlagRC') }}</q-item-label>
                            <div class="row q-gutter-xs q-mt-xs">
                                <div
                                    v-for="color in FlagColors"
                                    :key="color.name"
                                    class="color-picker-box cursor-pointer"
                                    :class="{ 'selected': contextMenuObject.colorFlag2 === color.name }"
                                    :style="{ backgroundColor: color.value, border: color.name === 'white' ? '1px solid #ccc' : 'none' }"
                                    @click="changeFlagColor(contextMenuObject!, color.name, 2)"
                                >
                                    <q-tooltip>{{ ts(color.name) }}</q-tooltip>
                                </div>
                            </div>
                        </q-item-section>
                    </q-item>
                    <q-separator v-if="contextMenuObject?.type === CourseObjectType.RaceLine" />

                    <q-item
                        v-if="contextMenuObject
                        && (contextMenuObject.type === CourseObjectType.Mark || contextMenuObject.type === CourseObjectType.Gate
                            || contextMenuObject.type === CourseObjectType.Boat || contextMenuObject.type === CourseObjectType.Text
                            || contextMenuObject.type === CourseObjectType.RaceLine)"
                        v-ripple
                        clickable
                        @click="editObject(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="edit" />
                        </q-item-section>
                        <q-item-section>
                            {{
                                contextMenuObject?.type === CourseObjectType.RaceLine
                                ? ts('editLabelRaceLine', { label: contextMenuObject.label || ts('line') })
                                : ts('edit')
                            }}
                        </q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine"
                        v-ripple
                        clickable
                        @click="toggleDirection(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="swap_vert" />
                        </q-item-section>
                        <q-item-section>{{ ts('toggleDirection') }}</q-item-section>
                    </q-item>
                    <q-item v-if="contextMenuObject?.type === CourseObjectType.RaceLine">
                        <q-item-section avatar>
                            <q-icon name="category" />
                        </q-item-section>
                        <q-item-section>
                            <div class="row q-gutter-xs q-mt-xs">
                                <q-btn
                                    v-for="shape in Shapes"
                                    :key="shape.name"
                                    flat
                                    round
                                    dense
                                    size="sm"
                                    color="black"
                                    @click="changeShape(contextMenuObject!, shape.name, false)"
                                >
                                    <q-icon
                                        v-if="shape.name === 'sphere'"
                                        :name="contextMenuObject?.shape === 'sphere' ? 'circle' : 'radio_button_unchecked'"
                                    />
                                    <q-icon
                                        v-else-if="shape.name === 'cylinder'"
                                        :name="contextMenuObject?.shape === 'cylinder' ? 'square' : 'crop_square'"
                                    />
                                    <q-icon
                                        v-else-if="shape.name === 'pyramid'"
                                        :name="contextMenuObject?.shape === 'pyramid' ? 'eject' : 'change_history'"
                                    />
                                    <q-tooltip>{{ ts(shape.name) }}</q-tooltip>
                                </q-btn>
                            </div>
                        </q-item-section>
                    </q-item>
                    <q-item v-if="contextMenuObject?.type === CourseObjectType.RaceLine">
                        <q-item-section avatar>
                            <q-icon name="palette" />
                        </q-item-section>
                        <q-item-section>
                            <div class="row q-gutter-xs q-mt-xs">
                                <div
                                    v-for="color in Colors"
                                    :key="color.name"
                                    class="color-picker-box cursor-pointer"
                                    :style="{ backgroundColor: getColorValue(color.name, CourseObjectType.Mark) }"
                                    @click="changeColor(contextMenuObject!, color.name, 'primary')"
                                >
                                    <q-tooltip>{{ ts(color.name) }}</q-tooltip>
                                </div>
                            </div>
                        </q-item-section>
                    </q-item>

                    <q-separator
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine
                        && (contextMenuObject.secondRaceline || contextMenuObject.sharedRaceline)"
                    />

                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine
                        && (contextMenuObject.secondRaceline || contextMenuObject.sharedRaceline)"
                        v-ripple
                        clickable
                        @click="editObject(contextMenuObject!, true)"
                    >
                        <q-item-section avatar>
                            <q-icon name="edit" />
                        </q-item-section>
                        <q-item-section>{{
                            ts('editLabelRaceLineSecond', { label: contextMenuObject.secondLabel || ts('line') })
                        }}</q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine
                        && (contextMenuObject.secondRaceline || contextMenuObject.sharedRaceline)"
                        v-ripple
                        clickable
                        @click="toggleDirection(contextMenuObject!, true)"
                    >
                        <q-item-section avatar>
                            <q-icon name="swap_vert" />
                        </q-item-section>
                        <q-item-section>{{ ts('toggleDirection') }}</q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine && contextMenuObject.secondRaceline"
                    >
                        <q-item-section avatar>
                            <q-icon name="category" />
                        </q-item-section>
                        <q-item-section>
                            <div class="row q-gutter-xs q-mt-xs">
                                <q-btn
                                    v-for="shape in Shapes"
                                    :key="shape.name"
                                    flat
                                    round
                                    dense
                                    size="sm"
                                    color="black"
                                    @click="changeShape(contextMenuObject!, shape.name, true)"
                                >
                                    <q-icon
                                        v-if="shape.name === 'sphere'"
                                        :name="contextMenuObject?.secondShape === 'sphere' ? 'circle' : 'radio_button_unchecked'"
                                    />
                                    <q-icon
                                        v-else-if="shape.name === 'cylinder'"
                                        :name="contextMenuObject?.secondShape === 'cylinder' ? 'square' : 'crop_square'"
                                    />
                                    <q-icon
                                        v-else-if="shape.name === 'pyramid'"
                                        :name="contextMenuObject?.secondShape === 'pyramid' ? 'eject' : 'change_history'"
                                    />
                                    <q-tooltip>{{ ts(shape.name) }}</q-tooltip>
                                </q-btn>
                            </div>
                        </q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine && contextMenuObject.secondRaceline"
                    >
                        <q-item-section avatar>
                            <q-icon name="palette" />
                        </q-item-section>
                        <q-item-section>
                            <div class="row q-gutter-xs q-mt-xs">
                                <div
                                    v-for="color in Colors"
                                    :key="color.name"
                                    class="color-picker-box cursor-pointer"
                                    :style="{ backgroundColor: getColorValue(color.name, CourseObjectType.Mark) }"
                                    @click="changeColor(contextMenuObject!, color.name, 'secondary')"
                                >
                                    <q-tooltip>{{ ts(color.name) }}</q-tooltip>
                                </div>
                            </div>
                        </q-item-section>
                    </q-item>

                    <q-separator v-if="contextMenuObject?.type === CourseObjectType.RaceLine" />

                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine && !contextMenuObject.secondRaceline
                        && !contextMenuObject.sharedRaceline"
                        v-ripple
                        clickable
                        @click="toggleSecondRaceLine(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="add_link" />
                        </q-item-section>
                        <q-item-section>{{ ts('addSecondRaceLine') }}</q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine && !contextMenuObject.sharedRaceline
                        && !contextMenuObject.secondRaceline"
                        v-ripple
                        clickable
                        @click="toggleSharedRaceLine(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="join_inner" />
                        </q-item-section>
                        <q-item-section>{{ ts('addSharedRaceLine') }}</q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine && contextMenuObject.sharedRaceline"
                        v-ripple
                        clickable
                        @click="separateRaceLines(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="call_split" />
                        </q-item-section>
                        <q-item-section>{{ ts('separateRaceLines') }}</q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.RaceLine && contextMenuObject.secondRaceline"
                        v-ripple
                        clickable
                        @click="toggleSecondRaceLine(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="link_off" />
                        </q-item-section>
                        <q-item-section>
                            {{
                                ts('onlyOneRaceLine', {
                                    label: contextMenuObject.label
                                        || (contextMenuObject.type === CourseObjectType.RaceLine ? (language === 'cs' ? 'ČÁRA' : 'LINE') : ''),
                                })
                            }}
                        </q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.Boat"
                        v-ripple
                        clickable
                        @click="resetRotation(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="navigation" />
                        </q-item-section>
                        <q-item-section>{{ ts('rotate') }}</q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.Text"
                        v-ripple
                        clickable
                        @click="resetRotation(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="navigation" />
                        </q-item-section>
                        <q-item-section>{{ ts('straightenText') }}</q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject
                        && (contextMenuObject.type === CourseObjectType.Mark || contextMenuObject.type === CourseObjectType.Gate
                            || contextMenuObject.type === CourseObjectType.Boat || contextMenuObject.type === CourseObjectType.Text)"
                    >
                        <q-item-section avatar>
                            <q-icon name="category" />
                        </q-item-section>
                        <q-item-section>
                            <div class="row q-gutter-xs q-mt-xs">
                                <q-btn
                                    v-for="shape in Shapes"
                                    :key="shape.name"
                                    flat
                                    round
                                    dense
                                    size="sm"
                                    color="black"
                                    @click="changeShape(contextMenuObject!, shape.name)"
                                >
                                    <q-icon
                                        v-if="shape.name === 'sphere'"
                                        :name="contextMenuObject?.shape === 'sphere' ? 'circle' : 'radio_button_unchecked'"
                                    />
                                    <q-icon
                                        v-else-if="shape.name === 'cylinder'"
                                        :name="contextMenuObject?.shape === 'cylinder' ? 'square' : 'crop_square'"
                                    />
                                    <q-icon
                                        v-else-if="shape.name === 'pyramid'"
                                        :name="contextMenuObject?.shape === 'pyramid' ? 'eject' : 'change_history'"
                                    />
                                    <q-tooltip>{{ ts(shape.name) }}</q-tooltip>
                                </q-btn>
                            </div>
                        </q-item-section>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject
                        && (contextMenuObject.type === CourseObjectType.Mark || contextMenuObject.type === CourseObjectType.Gate
                            || contextMenuObject.type === CourseObjectType.Boat || contextMenuObject.type === CourseObjectType.Text)"
                    >
                        <q-item-section avatar>
                            <q-icon name="palette" />
                        </q-item-section>
                        <q-item-section>
                            <div class="row q-gutter-xs q-mt-xs">
                                <div
                                    v-for="color in Colors"
                                    :key="color.name"
                                    class="color-picker-box cursor-pointer"
                                    :style="{ backgroundColor: getColorValue(color.name, contextMenuObject!.type) }"
                                    @click="changeColor(contextMenuObject!, color.name, 'primary')"
                                >
                                    <q-tooltip>{{ ts(color.name) }}</q-tooltip>
                                </div>
                            </div>
                        </q-item-section>
                    </q-item>

                    <q-separator
                        v-if="contextMenuObject
                        && (contextMenuObject.type === CourseObjectType.Mark || contextMenuObject.type === CourseObjectType.Gate
                            || contextMenuObject.type === CourseObjectType.Boat)"
                    />

                    <!-- Flag Colors Section (for Boat) -->
                    <q-item v-if="contextMenuObject?.type === CourseObjectType.Boat">
                        <q-item-section avatar>
                            <div class="relative-position">
                                <q-icon name="flag" color="grey-7" />
                                <div
                                    class="absolute-center text-white text-bold"
                                    style="font-size: 10px; margin-top: 1px"
                                >1</div>
                            </div>
                        </q-item-section>
                        <q-item-section>
                            <q-item-label caption>{{ ts('firstFlag') }}</q-item-label>
                            <div class="row q-gutter-xs q-mt-xs">
                                <div
                                    v-for="color in FlagColors"
                                    :key="color.name"
                                    class="color-picker-box cursor-pointer"
                                    :class="{ 'selected': contextMenuObject.colorFlag1 === color.name }"
                                    :style="{ backgroundColor: color.value, border: color.name === 'white' ? '1px solid #ccc' : 'none' }"
                                    @click="changeFlagColor(contextMenuObject!, color.name, 1)"
                                >
                                    <q-tooltip>{{ ts(color.name) }}</q-tooltip>
                                </div>
                            </div>
                        </q-item-section>
                    </q-item>
                    <q-item v-if="contextMenuObject?.type === CourseObjectType.Boat">
                        <q-item-section avatar>
                            <div class="relative-position">
                                <q-icon name="flag" color="grey-7" />
                                <div
                                    class="absolute-center text-white text-bold"
                                    style="font-size: 10px; margin-top: 1px"
                                >2</div>
                            </div>
                        </q-item-section>
                        <q-item-section>
                            <q-item-label caption>{{ ts('secondFlag') }}</q-item-label>
                            <div class="row q-gutter-xs q-mt-xs">
                                <div
                                    v-for="color in FlagColors"
                                    :key="color.name"
                                    class="color-picker-box cursor-pointer"
                                    :class="{ 'selected': contextMenuObject.colorFlag2 === color.name }"
                                    :style="{ backgroundColor: color.value, border: color.name === 'white' ? '1px solid #ccc' : 'none' }"
                                    @click="changeFlagColor(contextMenuObject!, color.name, 2)"
                                >
                                    <q-tooltip>{{ ts(color.name) }}</q-tooltip>
                                </div>
                            </div>
                        </q-item-section>
                    </q-item>
                    <q-separator v-if="contextMenuObject?.type === CourseObjectType.Boat" />

                    <q-item
                        v-if="contextMenuObject
                        && (contextMenuObject.type === CourseObjectType.Mark || contextMenuObject.type === CourseObjectType.Gate
                            || contextMenuObject.type === CourseObjectType.Boat)"
                        v-ripple
                        clickable
                        @click="toggleShowDirection(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon :name="contextMenuObject.showDirection ? 'visibility_off' : 'visibility'" />
                        </q-item-section>
                        <q-item-section>{{
                            contextMenuObject.showDirection ? ts('hideDirection') : ts('showDirection')
                        }}</q-item-section>
                    </q-item>

                    <q-item
                        v-if="contextMenuObject
                        && (contextMenuObject.type === CourseObjectType.Mark || contextMenuObject.type === CourseObjectType.Boat)"
                        v-ripple
                        clickable
                        @click="setDirection(contextMenuObject!, contextMenuObject.direction === 'left' ? 'right' : 'left')"
                    >
                        <q-item-section avatar>
                            <q-icon :name="contextMenuObject.direction === 'left' ? 'turn_right' : 'turn_left'" />
                        </q-item-section>
                        <q-item-section>{{
                            contextMenuObject.direction === 'left' ? ts('directionRight') : ts('directionLeft')
                        }}</q-item-section>
                    </q-item>

                    <q-item
                        v-if="contextMenuObject
                        && (contextMenuObject.type === CourseObjectType.Mark || contextMenuObject.type === CourseObjectType.Gate
                            || contextMenuObject.type === CourseObjectType.Boat)"
                        v-ripple
                        clickable
                        @mouseenter="qmenu_prev = true"
                        @mouseleave="qmenu_prev = false"
                    >
                        <q-item-section avatar>
                            <q-icon name="trending_flat" style="transform: rotate(180deg)" />
                        </q-item-section>
                        <q-item-section>{{ ts('prevDirection') }}</q-item-section>
                        <q-item-section side>
                            <q-icon name="keyboard_arrow_right" />
                        </q-item-section>
                        <q-menu
                            anchor="top end"
                            self="top start"
                            auto-close
                            @mouseenter="qmenu_prev = true"
                            @mouseleave="qmenu_prev = false"
                            v-model="qmenu_prev"
                        >
                            <q-list style="min-width: 150px">
                                <q-item
                                    v-ripple
                                    clickable
                                    :active="contextMenuObject.prevObjectId === null"
                                    @click="setPrevObject(contextMenuObject!, null)"
                                >
                                    <q-item-section avatar>
                                        <q-icon
                                            v-if="contextMenuObject.prevObjectId === null"
                                            name="check"
                                        />
                                    </q-item-section>
                                    <q-item-section>{{ ts('noPrevDirection') }}</q-item-section>
                                </q-item>
                                <q-separator />
                                <template
                                    v-for="obj in objects.filter(o =>
                                        o.id !== contextMenuObject!.id
                                        && [CourseObjectType.Mark, CourseObjectType.Gate, CourseObjectType.Boat, CourseObjectType.RaceLine].includes(o.type)
                                    )"
                                    :key="obj.id"
                                >
                                    <q-item
                                        v-ripple
                                        clickable
                                        :active="contextMenuObject.prevObjectId === obj.id && !contextMenuObject.prevObjectIdSecondary"
                                        @click="setPrevObject(contextMenuObject!, obj.id, false)"
                                    >
                                        <q-item-section avatar>
                                            <q-icon
                                                v-if="contextMenuObject.prevObjectId === obj.id && !contextMenuObject.prevObjectIdSecondary"
                                                name="check"
                                            />
                                        </q-item-section>
                                        <q-item-section>{{ getObjectName(obj) }}</q-item-section>
                                    </q-item>
                                    <q-item
                                        v-if="obj.type === CourseObjectType.RaceLine && obj.secondRaceline && !obj.sharedRaceline"
                                        v-ripple
                                        clickable
                                        :active="contextMenuObject.prevObjectId === obj.id && contextMenuObject.prevObjectIdSecondary"
                                        @click="setPrevObject(contextMenuObject!, obj.id, true)"
                                    >
                                        <q-item-section avatar>
                                            <q-icon
                                                v-if="contextMenuObject.prevObjectId === obj.id && contextMenuObject.prevObjectIdSecondary"
                                                name="check"
                                            />
                                        </q-item-section>
                                        <q-item-section>{{ obj.secondLabel || ts('line') }}</q-item-section>
                                    </q-item>
                                </template>
                            </q-list>
                        </q-menu>
                    </q-item>

                    <q-item
                        v-if="contextMenuObject
                        && (contextMenuObject.type === CourseObjectType.Mark || contextMenuObject.type === CourseObjectType.Gate
                            || contextMenuObject.type === CourseObjectType.Boat)"
                        v-ripple
                        clickable
                        @mouseenter="qmenu_next = true"
                        @mouseleave="qmenu_next = false"
                    >
                        <q-item-section avatar>
                            <q-icon name="trending_flat" />
                        </q-item-section>
                        <q-item-section>{{ ts('nextDirection') }}</q-item-section>
                        <q-item-section side>
                            <q-icon name="keyboard_arrow_right" />
                        </q-item-section>
                        <q-menu
                            anchor="top end"
                            self="top start"
                            auto-close
                            @mouseenter="qmenu_next = true"
                            @mouseleave="qmenu_next = false"
                            v-model="qmenu_next"
                        >
                            <q-list style="min-width: 150px">
                                <q-item
                                    v-ripple
                                    clickable
                                    :active="contextMenuObject.nextObjectId === null"
                                    @click="setNextObject(contextMenuObject!, null)"
                                >
                                    <q-item-section avatar>
                                        <q-icon
                                            v-if="contextMenuObject.nextObjectId === null"
                                            name="check"
                                        />
                                    </q-item-section>
                                    <q-item-section>{{ ts('noNextDirection') }}</q-item-section>
                                </q-item>
                                <q-separator />
                                <template
                                    v-for="obj in objects.filter(o =>
                                        o.id !== contextMenuObject!.id
                                        && [CourseObjectType.Mark, CourseObjectType.Gate, CourseObjectType.Boat, CourseObjectType.RaceLine].includes(o.type)
                                    )"
                                    :key="obj.id"
                                >
                                    <q-item
                                        v-ripple
                                        clickable
                                        :active="contextMenuObject.nextObjectId === obj.id && !contextMenuObject.nextObjectIdSecondary"
                                        @click="setNextObject(contextMenuObject!, obj.id, false)"
                                    >
                                        <q-item-section avatar>
                                            <q-icon
                                                v-if="contextMenuObject.nextObjectId === obj.id && !contextMenuObject.nextObjectIdSecondary"
                                                name="check"
                                            />
                                        </q-item-section>
                                        <q-item-section>{{ getObjectName(obj) }}</q-item-section>
                                    </q-item>
                                    <q-item
                                        v-if="obj.type === CourseObjectType.RaceLine && obj.secondRaceline && !obj.sharedRaceline"
                                        v-ripple
                                        clickable
                                        :active="contextMenuObject.nextObjectId === obj.id && contextMenuObject.nextObjectIdSecondary"
                                        @click="setNextObject(contextMenuObject!, obj.id, true)"
                                    >
                                        <q-item-section avatar>
                                            <q-icon
                                                v-if="contextMenuObject.nextObjectId === obj.id && contextMenuObject.nextObjectIdSecondary"
                                                name="check"
                                            />
                                        </q-item-section>
                                        <q-item-section>{{ obj.secondLabel || ts('line') }}</q-item-section>
                                    </q-item>
                                </template>
                            </q-list>
                        </q-menu>
                    </q-item>
                    <q-item
                        v-if="contextMenuObject?.type === CourseObjectType.Wind"
                        v-ripple
                        clickable
                        @click="resetWindPosition(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="restart_alt" />
                        </q-item-section>
                        <q-item-section>{{ ts('resetPosition') }}</q-item-section>
                    </q-item>
                    <q-separator />
                    <q-item
                        v-if="contextMenuObject && contextMenuObject.type !== CourseObjectType.Wind"
                        v-ripple
                        clickable
                        class="text-negative"
                        @click="removeObject(contextMenuObject!)"
                    >
                        <q-item-section avatar>
                            <q-icon name="delete" color="negative" />
                        </q-item-section>
                        <q-item-section>{{ ts('remove') }}</q-item-section>
                    </q-item>
                </q-list>

                <!-- New Object Menu (when clicking on empty space) -->
                <q-list style="min-width: 150px" v-else>
                    <q-item clickable v-ripple @click="addObjectAt(CourseObjectType.Mark)">
                        <q-item-section avatar>
                            <icon-mark />
                        </q-item-section>
                        <q-item-section>{{ ts('addMark') }}</q-item-section>
                    </q-item>
                    <q-item clickable v-ripple @click="addObjectAt(CourseObjectType.Gate)">
                        <q-item-section avatar>
                            <icon-gate />
                        </q-item-section>
                        <q-item-section>{{ ts('addGate') }}</q-item-section>
                    </q-item>
                    <q-item clickable v-ripple @click="addObjectAt(CourseObjectType.RaceLine)">
                        <q-item-section avatar>
                            <icon-race-line />
                        </q-item-section>
                        <q-item-section>{{ ts('addRaceLine') }}</q-item-section>
                    </q-item>
                    <q-item clickable v-ripple @click="addObjectAt(CourseObjectType.Boat)">
                        <q-item-section avatar>
                            <icon-boat />
                        </q-item-section>
                        <q-item-section>{{ ts('addBoat') }}</q-item-section>
                    </q-item>
                    <q-item clickable v-ripple @click="addObjectAt(CourseObjectType.Obstacle)">
                        <q-item-section avatar>
                            <icon-obstacle />
                        </q-item-section>
                        <q-item-section>{{ ts('addObstacle') }}</q-item-section>
                    </q-item>
                    <q-item clickable v-ripple @click="addObjectAt(CourseObjectType.Text)">
                        <q-item-section avatar>
                            <icon-text />
                        </q-item-section>
                        <q-item-section>{{ ts('addText') }}</q-item-section>
                    </q-item>
                </q-list>
            </q-menu>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { ts } from '@/plugins/i18nFormat'
import { useQuasar } from 'quasar'
import IconMark from '@/components/icons/IconMark.vue'
import IconGate from '@/components/icons/IconGate.vue'
import IconBoat from '@/components/icons/IconBoat.vue'
import IconRaceLine from '@/components/icons/IconRaceLine.vue'
import IconObstacle from '@/components/icons/IconObstacle.vue'
import IconText from '@/components/icons/IconText.vue'
import { useRepo } from 'pinia-orm'
import EventCourseObject, { CourseObjectType } from '@/orm/models/EventCourseObject'
import EventCourseModel from '@/orm/models/EventCourse'

interface Props {
    eventId: number
    courseTitle?: string
}

const props = defineProps<Props>()
const quasar = useQuasar()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const canvasWidth = ref(800)
const canvasHeight = ref(600)
const zoom = ref(1.0)

function setZoom(newZoom: number) {
    const canvas = canvasRef.value
    if (!canvas) {
        zoom.value = newZoom
        draw()
        return
    }

    const container = canvas.parentElement
    if (!container || container.clientWidth === 0) {
        zoom.value = newZoom
        draw()
        return
    }

    // Record the center point in canvas coordinates (at current zoom)
    // We want to keep the point that is currently in the middle of the viewport
    // at the same relative position in the middle of the viewport after zoom.
    const viewCenterX = container.scrollLeft + container.clientWidth / 2
    const viewCenterY = container.scrollTop // Keep top edge as reference as requested

    const canvasCenterX = viewCenterX / zoom.value
    const canvasCenterY = viewCenterY / zoom.value

    zoom.value = newZoom

    // We must update the canvas size BEFORE scrolling, so container.scrollWidth/Height are updated
    updateCanvasSize()

    // Update the actual canvas.width/height so CSS size updates immediately
    // and browser recalculates scrollable area
    canvas.width = canvasWidth.value
    canvas.height = canvasHeight.value
    canvas.style.width = `${canvasWidth.value}px`
    canvas.style.height = `${canvasHeight.value}px`

    // Force browser to layout so scrollWidth/scrollHeight are definitely correct
    void canvas.offsetHeight
    void container.offsetHeight // Added this to be sure container layout is also refreshed

    // New scroll position to keep the same point at the same viewport position
    const targetScrollLeft = Math.round(canvasCenterX * zoom.value - container.clientWidth / 2)
    const targetScrollTop = Math.round(canvasCenterY * zoom.value)

    container.scrollLeft = targetScrollLeft
    container.scrollTop = targetScrollTop

    draw()

    // Sometimes the browser needs a tiny bit of time or another layout pass
    // to actually apply the scroll if the scrollable area just changed.
    requestAnimationFrame(() => {
        container.scrollLeft = targetScrollLeft
        container.scrollTop = targetScrollTop
        draw()
    })
}

function handleWheel(event: WheelEvent) {
    if (event.ctrlKey) {
        if (event.deltaY < 0) {
            zoomIn()
        } else if (event.deltaY > 0) {
            zoomOut()
        }
        event.preventDefault()
    }
}

function zoomIn() {
    if (zoom.value < 2.0) {
        setZoom(zoom.value + 0.25)
    }
}

function zoomOut() {
    if (zoom.value > 0.25) {
        setZoom(zoom.value - 0.25)
    }
}

function resetZoom() {
    setZoom(1.0)
}

const courseRepo = useRepo(EventCourseModel)
const objectsRepo = useRepo(EventCourseObject)
const qmenu_prev = ref(false)
const qmenu_next = ref(false)

// Drag and drop state
const isDragging = ref(false)
const contextMenuPosition = ref({ x: 0, y: 0 })
const draggedObjectId = ref<string | null>(null)
const dragTarget = ref<'primary' | 'secondary'>('primary')
const dragOffset = ref({ x: 0, y: 0 })
const dragStartPos = ref({ x: 0, y: 0 })
const draggedObjectPosition = ref({ x: 0, y: 0 })

// Selection and Rotation state
const isRotating = ref(false)
const selectedObjectId = ref<string | null>(null)
const rotatingObjectRotation = ref(0)

// Context menu state
const contextMenuVisible = ref(false)
const contextMenuObject = ref<EventCourseObject | null>(null)

const SELECTED_COLOR = '#1976D2'

const Colors = [
    { name: 'white', value: '#FFFFFF' },
    { name: 'yellow', value: '#FFFF00' },
    { name: 'orange', value: '#FFA500' },
    { name: 'pink', value: '#FFC0CB' },
    { name: 'red', value: '#FF0000' },
    { name: 'cyan', value: '#00FFFF' },
    { name: 'blue', value: '#0000FF' },
    { name: 'lightgreen', value: '#00FF00' },
]

const FlagColors = [
    { name: 'none', value: '#CCCCCC' },
    { name: 'white', value: '#FFFFFF' },
    { name: 'yellow', value: '#FFFF00' },
    { name: 'orange', value: '#FFA500' },
    { name: 'red', value: '#FF0000' },
    { name: 'blue', value: '#0000FF' },
    { name: 'lightgreen', value: '#00FF00' },
]

const Shapes = [
    { name: 'sphere' },
    { name: 'cylinder' },
    { name: 'pyramid' },
]

function getColorValue(colorName: string, type: CourseObjectType) {
    if (type === CourseObjectType.Text && colorName === 'white') return '#000000'
    const color = Colors.find(c => c.name === colorName)
    if (!color) return '#FFFFFF'
    if (colorName === 'cyan') return '#00FFFF'
    if (colorName === 'lightgreen') return '#00FF00'
    return color.value
}

function getContrastColor(colorName: string) {
    if (colorName === 'blue') return '#FFFFFF'
    return '#000000'
}

// Get or create course for this event
const course = computed(() => {
    let c: EventCourseModel | null
    if (props.courseTitle) {
        c = courseRepo.where('eventId', props.eventId).where('title', props.courseTitle).first()
    } else {
        c = courseRepo.where('eventId', props.eventId).first()
    }

    if (!c) {
        c = courseRepo.save({ eventId: props.eventId, title: props.courseTitle || 'A' })
    }

    return courseRepo.with('objects').find(c.id)
})

const objects = computed(() => course.value?.objects || [])

function addObject(type: CourseObjectType, atX?: number, atY?: number) {
    if (!course.value) return

    if (type === CourseObjectType.Wind && objects.value.some(obj => obj.type === CourseObjectType.Wind)) {
        return
    }

    let label = ''
    let secondLabel = ''

    if (type === CourseObjectType.Text) {
        label = ts('addText')
    } else if (type === CourseObjectType.RaceLine) {
        const checkLabel = (label: string) => {
            const startStr = ts('start').toUpperCase()
            const finishStr = ts('finish').toUpperCase()
            const upper = label.toUpperCase()
            return { isStart: upper === startStr, isFinish: upper === finishStr }
        }

        let hasStart = false
        let hasFinish = false

        objects.value.forEach(obj => {
            if (obj.type === CourseObjectType.RaceLine) {
                const l1 = checkLabel(obj.label)
                const l2 = checkLabel(obj.secondLabel)
                if (l1.isStart || l2.isStart) hasStart = true
                if (l1.isFinish || l2.isFinish) hasFinish = true
            }
        })

        if (!hasStart) {
            label = ts('start')
        } else if (!hasFinish) {
            label = ts('finish')
        } else {
            label = ts('line')
        }

        // Initialize secondLabel if we decide to enable it later or for default state
        const hasGlobalFinish = objects.value.some(obj => {
            if (obj.type !== CourseObjectType.RaceLine) return false
            const l1 = checkLabel(obj.label)
            const l2 = checkLabel(obj.secondLabel)
            // We want to know if after adding 'label' as first branch, we still need a FINISH somewhere
            return l1.isFinish || l2.isFinish || (label.toUpperCase() === ts('finish').toUpperCase())
        })

        if (!hasGlobalFinish) {
            secondLabel = ts('finish')
        } else {
            secondLabel = ts('line')
        }
    } else if (type === CourseObjectType.Mark || type === CourseObjectType.Gate) {
        let max = 0
        objects.value.forEach(obj => {
            if (obj.type === CourseObjectType.Mark || obj.type === CourseObjectType.Gate) {
                const match = obj.label.match(/^(\d+)/)

                if (match) {
                    const val = parseInt(match[1])

                    if (val > max) max = val
                }
            }
        })
        label = String(max + 1)
    }

    const canvas = canvasRef.value
    const container = canvas?.parentElement

    let defaultX = atX !== undefined ? atX : 400
    let defaultY = atY !== undefined ? atY : 300

    if (container) {
        // Use visible center of the container
        defaultX = container.scrollLeft + container.clientWidth / 2
        defaultY = container.scrollTop + container.clientHeight / 2

        // If we are placing a gate/raceline, we should center the WHOLE object, not just its first point.
        // Gates are 150px wide by default.
        if (type === CourseObjectType.Gate || type === CourseObjectType.RaceLine) {
            defaultX -= 75
        }
    }

    const startX = atX !== undefined ? atX : defaultX
    const startY = atY !== undefined ? atY : defaultY

    const newObj = objectsRepo.save({
        eventCourseId: course.value.id,
        eventId: props.eventId,
        type,
        x: startX,
        y: startY,
        x2: (type === CourseObjectType.Gate || type === CourseObjectType.RaceLine)
            ? startX + 150
            : (type === CourseObjectType.Obstacle ? startX + 40 : 0),
        y2: (type === CourseObjectType.Gate || type === CourseObjectType.RaceLine)
            ? startY
            : (type === CourseObjectType.Obstacle ? startY + 60 : 0),
        x3: (type === CourseObjectType.RaceLine) ? startX - 150 : 0,
        y3: (type === CourseObjectType.RaceLine) ? startY : 0,
        label,
        secondLabel,
        secondRaceline: false,
        sharedRaceline: false,
        shape: 'sphere',
        secondShape: 'sphere',
        color: 'white',
        secondColor: 'white',
        thirdColor: 'white',
        colorFlag1: type === CourseObjectType.RaceLine
            ? (!objects.value.some(o => o.type === CourseObjectType.RaceLine) ? 'orange' : 'blue')
            : 'none',
        colorFlag2: 'none',
        reversedDirection: true, // Default to "up" (against wind) for new lines
    })

    if (newObj) {
        if (type === CourseObjectType.Text) {
            editObject(newObj)
        }

        // Try to guess the previous track point
        let prevObj: EventCourseObject | null = null
        let isSecondary = false

        if (type === CourseObjectType.Mark || type === CourseObjectType.Gate) {
            const currentNum = parseInt(label)
            if (!Number.isNaN(currentNum)) {
                if (currentNum > 1) {
                    // Look for X-1
                    const prevLabel = String(currentNum - 1)
                    prevObj = objects.value.find(o =>
                        (o.type === CourseObjectType.Mark || o.type === CourseObjectType.Gate) && o.label === prevLabel
                    ) || null
                } else if (currentNum === 1) {
                    // Look for RaceLine "Start"
                    const startStr = ts('start').toUpperCase()
                    prevObj = objects.value.find(o => {
                        if (o.type !== CourseObjectType.RaceLine) return false
                        return o.label.toUpperCase() === startStr || o.secondLabel.toUpperCase() === startStr
                    }) || null
                    if (
                        prevObj && prevObj.secondLabel.toUpperCase() === startStr && prevObj.secondRaceline
                        && !prevObj.sharedRaceline
                    ) {
                        isSecondary = true
                    }
                }
            }
        } else if (type === CourseObjectType.RaceLine) {
            // Look for Mark/Gate with highest number
            let maxNum = -1
            let candidate: EventCourseObject | null = null
            objects.value.forEach(o => {
                if (o.type === CourseObjectType.Mark || o.type === CourseObjectType.Gate) {
                    const match = o.label.match(/^(\d+)/)
                    if (match) {
                        const val = parseInt(match[1])
                        if (val > maxNum) {
                            maxNum = val
                            candidate = o
                        }
                    }
                }
            })
            prevObj = candidate
        }

        // Only link if the previous object doesn't have a next object set yet
        if (prevObj && !prevObj.nextObjectId) {
            setPrevObject(newObj, prevObj.id, isSecondary)
        }
    }

    const event = useRepo(EventCourseModel).with('event').find(course.value.id)?.event

    if (event) {
        event.setModified()
    }

    draw()
}

function removeObject(obj: EventCourseObject) {
    if (!course.value || obj.type === CourseObjectType.Wind) return

    // Set all nextObject and prevObject references to null before deleting
    objectsRepo.where('eventCourseId', course.value.id).where('nextObjectId', obj.id).update({
        nextObjectId: null,
        nextObjectIdSecondary: false,
    })
    objectsRepo.where('eventCourseId', course.value.id).where('prevObjectId', obj.id).update({
        prevObjectId: null,
        prevObjectIdSecondary: false,
    })

    objectsRepo.destroy(obj.id)

    const event = useRepo(EventCourseModel).with('event').find(course.value.id)?.event

    if (event) {
        event.setModified()
    }

    draw()
}

function removeAllObjects() {
    if (!course.value) return

    // Set all nextObject and prevObject references to null before deleting
    objectsRepo.where('eventCourseId', course.value.id).update({
        nextObjectId: null,
        nextObjectIdSecondary: false,
        prevObjectId: null,
        prevObjectIdSecondary: false,
    })

    objectsRepo.where('eventCourseId', course.value.id).where('type', (type: string) => type !== CourseObjectType.Wind)
        .delete()

    // Reset wind position to default
    const wind = objectsRepo.where('eventCourseId', course.value.id).where('type', CourseObjectType.Wind).first()
    if (wind) {
        objectsRepo.save({
            ...wind,
            x: canvasRef.value && canvasRef.value.width > 100 ? (canvasRef.value.width * 3) / 4 : 600,
            y: 50,
        })
    }

    // Reset all RaceLine shared states
    objectsRepo.where('eventCourseId', course.value.id).where('type', CourseObjectType.RaceLine).update({
        sharedRaceline: false,
    })

    const event = useRepo(EventCourseModel).with('event').find(course.value.id)?.event

    if (event) {
        event.setModified()
    }

    draw()
}

function generateExportCanvas() {
    const canvas = canvasRef.value
    if (!canvas || objects.value.length === 0) return null

    // 1. Calculate bounding box of all objects
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    objects.value.forEach(obj => {
        let bounds: { minX: number; minY: number; maxX: number; maxY: number }

        switch (obj.type) {
            case CourseObjectType.Mark: {
                // Radius is 20, add 1.5x for direction arrows (20 * 1.5 = 30 extra)
                const r = 20 + 30
                bounds = { minX: obj.x - r, minY: obj.y - r, maxX: obj.x + r, maxY: obj.y + r }
                break
            }
            case CourseObjectType.Gate: {
                // Two marks with radius 20, add 1.5x for direction arrows
                const r = 20 + 30
                bounds = {
                    minX: Math.min(obj.x, obj.x2) - r,
                    minY: Math.min(obj.y, obj.y2) - r,
                    maxX: Math.max(obj.x, obj.x2) + r,
                    maxY: Math.max(obj.y, obj.y2) + r,
                }
                break
            }
            case CourseObjectType.RaceLine: {
                // Boat at (x,y) with approx r=32, marks at (x2,y2) and (x3,y3) with r=20
                // Add 1.5x (30px) to marks and same margin to boat for arrows
                const mr = 20 + 30
                const br = 32 + 30
                bounds = {
                    minX: Math.min(
                        obj.x - br,
                        obj.x2 - mr,
                        (obj.secondRaceline || obj.sharedRaceline) ? obj.x3 - mr : obj.x2 - mr,
                    ),
                    minY: Math.min(
                        obj.y - br,
                        obj.y2 - mr,
                        (obj.secondRaceline || obj.sharedRaceline) ? obj.y3 - mr : obj.y2 - mr,
                    ),
                    maxX: Math.max(
                        obj.x + br,
                        obj.x2 + mr,
                        (obj.secondRaceline || obj.sharedRaceline) ? obj.x3 + mr : obj.x2 + mr,
                    ),
                    maxY: Math.max(
                        obj.y + br,
                        obj.y2 + mr,
                        (obj.secondRaceline || obj.sharedRaceline) ? obj.y3 + mr : obj.y2 + mr,
                    ),
                }
                break
            }
            case CourseObjectType.Boat: {
                // Boat height is 64 (from -40 to 24), width is 28.8 (from -14.4 to 14.4)
                // Approximate with radius of half the height for rotation, add margin for arrows
                const r = 32 + 30
                bounds = { minX: obj.x - r, minY: obj.y - r, maxX: obj.x + r, maxY: obj.y + r }
                break
            }
            case CourseObjectType.Obstacle:
                bounds = {
                    minX: Math.min(obj.x, obj.x + obj.x2),
                    minY: Math.min(obj.y, obj.y + obj.y2),
                    maxX: Math.max(obj.x, obj.x + obj.x2),
                    maxY: Math.max(obj.y, obj.y + obj.y2),
                }
                break
            case CourseObjectType.Text: {
                // Hard to measure text precisely, use an approximation based on label length
                // fontSize is up to 28px
                const textWidth = (obj.label || ts('addText')).length * 15
                const textHeight = 30
                const r = Math.sqrt(textWidth * textWidth + textHeight * textHeight) / 2
                bounds = { minX: obj.x - r, minY: obj.y - r, maxX: obj.x + r, maxY: obj.y + r }
                break
            }
            case CourseObjectType.Wind: {
                // Vertical arrow 128px long (centered horizontally at x)
                // Head width is 60, so arrow itself spans from x-30 to x+30
                // Label "Wind" starts at x + headWidth/2 + 10 = x + 40
                const textWidth = ts('wind').length * 15 // Approximation
                bounds = {
                    minX: obj.x - 30,
                    minY: obj.y,
                    maxX: obj.x + 40 + textWidth,
                    maxY: obj.y + 128,
                }
                break
            }
            default:
                bounds = { minX: obj.x, minY: obj.y, maxX: obj.x, maxY: obj.y }
        }

        minX = Math.min(minX, bounds.minX)
        minY = Math.min(minY, bounds.minY)
        maxX = Math.max(maxX, bounds.maxX)
        maxY = Math.max(maxY, bounds.maxY)
    })

    // Add 20px padding
    const padding = 20
    const exportX = minX - padding
    const exportY = minY - padding
    const exportWidth = (maxX - minX) + 2 * padding
    const exportHeight = (maxY - minY) + 2 * padding

    // Create a temporary canvas for export
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = exportWidth
    tempCanvas.height = exportHeight
    const ctx = tempCanvas.getContext('2d')
    if (!ctx) return null

    // Draw solid white background
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height)

    // Shift coordinates to match the cropped area
    ctx.translate(-exportX, -exportY)

    // Draw objects (simplified version of draw() without UI decorations)
    objects.value.forEach(obj => {
        ctx.strokeStyle = '#000'
        ctx.fillStyle = '#000'
        ctx.lineWidth = 2.8

        const x = obj.x
        const y = obj.y
        const x2 = obj.x2
        const y2 = obj.y2
        const x3 = obj.x3
        const y3 = obj.y3
        const rotation = obj.rotation

        switch (obj.type) {
            case CourseObjectType.Mark:
                drawDirection(ctx, obj)
                drawMark(ctx, x, y, obj.label, obj.color, obj.shape)
                break
            case CourseObjectType.Gate:
                drawDirection(ctx, obj)
                drawGate(ctx, x, y, x2, y2, obj.label, false, obj.color, obj.shape)
                break
            case CourseObjectType.RaceLine:
                drawDirection(ctx, obj)
                drawRaceLine(
                    ctx,
                    x,
                    y,
                    x2,
                    y2,
                    obj.label,
                    false,
                    obj.color,
                    obj.shape,
                    x3,
                    y3,
                    obj.secondLabel,
                    obj.secondRaceline,
                    obj.secondColor,
                    obj.secondShape,
                    obj.thirdColor,
                    obj.reversedDirection,
                    obj.secondReversedDirection,
                    obj.sharedRaceline,
                    obj.colorFlag1,
                    obj.colorFlag2,
                )
                break
            case CourseObjectType.Boat:
                drawDirection(ctx, obj)
                drawBoat(
                    ctx,
                    x,
                    y,
                    rotation,
                    obj.label,
                    false,
                    obj.color,
                    false,
                    obj.colorFlag1,
                    obj.colorFlag2,
                    obj.direction,
                )
                break
            case CourseObjectType.Obstacle:
                drawObstacle(ctx, x, y, x2, y2, false)
                break
            case CourseObjectType.Text:
                drawText(ctx, x, y, rotation, obj.label, false, obj.color)
                break
            case CourseObjectType.Wind:
                drawWind(ctx, x, y, false)
                break
        }
    })

    return tempCanvas
}

function exportAsImage() {
    const tempCanvas = generateExportCanvas()
    if (!tempCanvas) return

    // Download the image
    const link = document.createElement('a')
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    link.download = `track-${props.courseTitle || 'A'}-${timestamp}.png`
    link.href = tempCanvas.toDataURL('image/png')
    link.click()
}

function copyToClipboard() {
    const tempCanvas = generateExportCanvas()
    if (!tempCanvas) return

    tempCanvas.toBlob(blob => {
        if (!blob) return
        const item = new ClipboardItem({ 'image/png': blob })
        navigator.clipboard.write([item]).then(() => {
            quasar.notify({
                type: 'positive',
                message: ts('copiedToClipboard'),
                timeout: 1000,
            })
        }).catch(err => {
            console.log('Failed to copy image: ', err)
            quasar.notify({
                type: 'negative',
                message: ts('copyToClipboardFailed'),
            })
        })
    }, 'image/png')
}

function drawWind(ctx: CanvasRenderingContext2D, x: number, y: number, isSelected?: boolean) {
    ctx.save()
    ctx.translate(x, y)

    if (isSelected) {
        ctx.strokeStyle = SELECTED_COLOR
        ctx.lineWidth = 3.5
    } else {
        ctx.strokeStyle = '#000000'
        ctx.lineWidth = 2.8
    }

    ctx.fillStyle = '#FFFFFF'

    // Arrow (2x boat height = 2 * 64 = 128px)
    const length = 128
    const width = 30
    const headLength = 40
    const headWidth = 60

    ctx.beginPath()
    ctx.moveTo(-width / 2, 0)
    ctx.lineTo(width / 2, 0)
    ctx.lineTo(width / 2, length - headLength)
    ctx.lineTo(headWidth / 2, length - headLength)
    ctx.lineTo(0, length)
    ctx.lineTo(-headWidth / 2, length - headLength)
    ctx.lineTo(-width / 2, length - headLength)
    ctx.closePath()

    ctx.fill()
    ctx.stroke()

    // Label "Wind"
    ctx.fillStyle = isSelected ? SELECTED_COLOR : '#000000'
    ctx.font = '20px Roboto, sans-serif'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.fillText(ts('wind'), headWidth / 2 + 10, length / 3)

    ctx.restore()
}

function drawDirection(ctx: CanvasRenderingContext2D, obj: EventCourseObject) {
    if (!obj.showDirection || !obj.nextObjectId || !obj.prevObjectId) return

    const getActualObj = (id: string) => {
        const original = objects.value.find(o => o.id === id)
        if (!original) return null
        if (isDragging.value && draggedObjectId.value === id) {
            const dx = draggedObjectPosition.value.x - original.x
            const dy = draggedObjectPosition.value.y - original.y

            let x = original.x
            let y = original.y
            let x2 = original.x2
            let y2 = original.y2
            let x3 = original.x3
            let y3 = original.y3

            if (original.type === CourseObjectType.Obstacle && dragTarget.value === 'primary') {
                x = draggedObjectPosition.value.x
                y = draggedObjectPosition.value.y
            } else if (original.type === CourseObjectType.Obstacle && dragTarget.value === 'secondary') {
                x2 = draggedObjectPosition.value.x
                y2 = draggedObjectPosition.value.y
            } else if (dragTarget.value === 'primary') {
                x = draggedObjectPosition.value.x
                y = draggedObjectPosition.value.y
                x2 = original.x2 + dx
                y2 = original.y2 + dy
                x3 = original.x3 + dx
                y3 = original.y3 + dy
            } else if (dragTarget.value === 'secondary') {
                x2 = draggedObjectPosition.value.x
                y2 = draggedObjectPosition.value.y
            } else if (dragTarget.value === 'tertiary') {
                x3 = draggedObjectPosition.value.x
                y3 = draggedObjectPosition.value.y
            }
            return { ...original, x, y, x2, y2, x3, y3 }
        }
        return original
    }

    const prevObj = getActualObj(obj.prevObjectId)
    const nextObj = getActualObj(obj.nextObjectId)

    if (!prevObj || !nextObj) return

    const getTargetPos = (target: EventCourseObject, isSecondary: boolean) => {
        if (
            target.type === CourseObjectType.Mark || target.type === CourseObjectType.Boat
            || target.type === CourseObjectType.Wind || target.type === CourseObjectType.Text
        ) {
            return { x: target.x, y: target.y }
        }
        if (target.type === CourseObjectType.Gate) {
            // Midpoint of gate
            return { x: (target.x + target.x2) / 2, y: (target.y + target.y2) / 2 }
        }
        if (target.type === CourseObjectType.RaceLine) {
            if (isSecondary && target.secondRaceline) {
                // Midpoint of second line
                return { x: (target.x2 + target.x3) / 2, y: (target.y2 + target.y3) / 2 }
            }
            // Midpoint of first line
            return { x: (target.x + target.x2) / 2, y: (target.y + target.y2) / 2 }
        }
        return { x: target.x, y: target.y }
    }

    const getDirectionAngle = (
        target: EventCourseObject,
        isSecondary: boolean,
        fromPoint: { x: number; y: number },
        isDeparture: boolean,
    ) => {
        let p1 = { x: target.x, y: target.y }
        let p2 = { x: target.x2, y: target.y2 }

        if (target.type === CourseObjectType.RaceLine && isSecondary && target.secondRaceline) {
            p1 = { x: target.x2, y: target.y2 }
            p2 = { x: target.x3, y: target.y3 }
        }

        if (target.type !== CourseObjectType.Gate && target.type !== CourseObjectType.RaceLine) {
            return isDeparture
                ? Math.atan2(target.y - fromPoint.y, target.x - fromPoint.x)
                : Math.atan2(fromPoint.y - target.y, fromPoint.x - target.x)
        }

        // Perpendicular (normal) angle
        let angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) + Math.PI / 2

        // Midpoint
        const midX = (p1.x + p2.x) / 2
        const midY = (p1.y + p2.y) / 2

        // Check if fromPoint is within the segment's "corridor"
        const dx = p2.x - p1.x
        const dy = p2.y - p1.y
        const lenSq = dx * dx + dy * dy
        if (lenSq === 0) return angle

        const t = ((fromPoint.x - p1.x) * dx + (fromPoint.y - p1.y) * dy) / lenSq

        if (t < 0 || t > 1) {
            // Outside - use angle to midpoint
            angle = isDeparture
                ? Math.atan2(midY - fromPoint.y, midX - fromPoint.x)
                : Math.atan2(fromPoint.y - midY, fromPoint.x - midX)
        } else {
            // Inside - use normal
            // Choose the side towards/from fromPoint
            const dot = (fromPoint.x - midX) * Math.cos(angle) + (fromPoint.y - midY) * Math.sin(angle)
            if (isDeparture) {
                if (dot > 0) angle += Math.PI
            } else {
                if (dot < 0) angle += Math.PI
            }
        }
        return angle
    }

    const snapToOrthogonal = (angle: number) => {
        const threshold = 5 * (Math.PI / 180)
        const targets = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2, 2 * Math.PI]
        const normalizedAngle = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)

        for (const target of targets) {
            if (Math.abs(normalizedAngle - target) < threshold) {
                return target % (2 * Math.PI)
            }
        }
        return angle
    }

    const drawSingleDirection = (
        center: { x: number; y: number },
        arrivalAngle: number,
        departureAngle: number,
        direction: string,
        shape?: string,
    ) => {
        const isLeft = direction === 'left'
        const side = isLeft ? 1 : -1

        const r = 22 // Base radius from drawMark
        let arrowR = r * 1.5
        if (shape === 'cylinder' || shape === 'pyramid') {
            arrowR += 5
        }
        const straightLen = r * 2.5

        // Arrival tangent point on arrowR
        const arrivalTangentAngle = arrivalAngle + (Math.PI / 2) * side
        const arrivalPoint = {
            x: center.x + arrowR * Math.cos(arrivalTangentAngle),
            y: center.y + arrowR * Math.sin(arrivalTangentAngle),
        }

        // Departure tangent point on arrowR
        const departureTangentAngle = departureAngle + (Math.PI / 2) * side
        const departurePoint = {
            x: center.x + arrowR * Math.cos(departureTangentAngle),
            y: center.y + arrowR * Math.sin(departureTangentAngle),
        }

        ctx.save()
        ctx.strokeStyle = '#000'
        ctx.lineWidth = 2
        ctx.beginPath()

        // Arrival straight line
        ctx.moveTo(
            arrivalPoint.x - straightLen * Math.cos(arrivalAngle),
            arrivalPoint.y - straightLen * Math.sin(arrivalAngle),
        )
        ctx.lineTo(arrivalPoint.x, arrivalPoint.y)

        // Arc
        let startAngle = arrivalTangentAngle
        let endAngle = departureTangentAngle

        // Normalize angles for arc to prevent 360+ degree turns
        const twoPi = Math.PI * 2
        startAngle = ((startAngle % twoPi) + twoPi) % twoPi
        endAngle = ((endAngle % twoPi) + twoPi) % twoPi

        const isStraight = Math.abs(((startAngle - endAngle + Math.PI) % twoPi) - Math.PI) < 0.001

        if (isStraight) {
            // Just continue the line if it's a straight passage
            ctx.lineTo(departurePoint.x, departurePoint.y)
        } else {
            if (isLeft) {
                if (endAngle >= startAngle) endAngle -= twoPi
            } else {
                if (endAngle <= startAngle) endAngle += twoPi
            }
            ctx.arc(center.x, center.y, arrowR, startAngle, endAngle, isLeft)
        }

        // Departure straight line
        ctx.lineTo(
            departurePoint.x + straightLen * Math.cos(departureAngle),
            departurePoint.y + straightLen * Math.sin(departureAngle),
        )
        ctx.stroke()

        // Arrow head
        const headLen = 10
        const endX = departurePoint.x + straightLen * Math.cos(departureAngle)
        const endY = departurePoint.y + straightLen * Math.sin(departureAngle)

        ctx.beginPath()
        ctx.moveTo(endX, endY)
        ctx.lineTo(
            endX - headLen * Math.cos(departureAngle - Math.PI / 6),
            endY - headLen * Math.sin(departureAngle - Math.PI / 6),
        )
        ctx.moveTo(endX, endY)
        ctx.lineTo(
            endX - headLen * Math.cos(departureAngle + Math.PI / 6),
            endY - headLen * Math.sin(departureAngle + Math.PI / 6),
        )
        ctx.stroke()
        ctx.restore()
    }

    if (obj.type === CourseObjectType.Mark || obj.type === CourseObjectType.Boat) {
        const arrivalAngle = snapToOrthogonal(
            getDirectionAngle(prevObj, obj.prevObjectIdSecondary, { x: obj.x, y: obj.y }, false),
        )
        const departureAngle = snapToOrthogonal(
            getDirectionAngle(nextObj, obj.nextObjectIdSecondary, { x: obj.x, y: obj.y }, true),
        )
        drawSingleDirection({ x: obj.x, y: obj.y }, arrivalAngle, departureAngle, obj.direction, obj.shape)
    } else if (obj.type === CourseObjectType.Gate) {
        const mid = { x: (obj.x + obj.x2) / 2, y: (obj.y + obj.y2) / 2 }

        // Perpendicular angle to the gate line
        const gateAngle = Math.atan2(obj.y2 - obj.y, obj.x2 - obj.x)
        const normalAngle = snapToOrthogonal(gateAngle + Math.PI / 2)

        // Determine side of arrival from previous object
        const prevPos = getTargetPos(prevObj, obj.prevObjectIdSecondary)
        let arrivalAngle = normalAngle
        const arrivalDot = (obj.x - prevPos.x) * Math.cos(arrivalAngle) + (obj.y - prevPos.y) * Math.sin(arrivalAngle)
        if (arrivalDot < 0) arrivalAngle = (arrivalAngle + Math.PI) % (2 * Math.PI)

        // Determine side of departure to next object
        const nextPos = getTargetPos(nextObj, obj.nextObjectIdSecondary)
        let departureAngle = normalAngle
        const departureDot = (nextPos.x - obj.x) * Math.cos(departureAngle)
            + (nextPos.y - obj.y) * Math.sin(departureAngle)
        if (departureDot < 0) departureAngle = (departureAngle + Math.PI) % (2 * Math.PI)

        const vArr = { x: Math.cos(arrivalAngle), y: Math.sin(arrivalAngle) }
        const vRight = { x: -vArr.y, y: vArr.x }
        // Use dot product to determine relative position of p2 to p1 from vArr's perspective
        const dot = (obj.x2 - obj.x) * vRight.x + (obj.y2 - obj.y) * vRight.y

        if (dot > 0) {
            // p2 is to the right of p1 from arrival's perspective
            // "Right mark starboard" (Starboard)
            drawSingleDirection({ x: obj.x, y: obj.y }, arrivalAngle, departureAngle, 'left', obj.shape)
            drawSingleDirection({ x: obj.x2, y: obj.y2 }, arrivalAngle, departureAngle, 'right', obj.shape)
        } else {
            // p1 is to the right of p2
            drawSingleDirection({ x: obj.x2, y: obj.y2 }, arrivalAngle, departureAngle, 'left', obj.shape)
            drawSingleDirection({ x: obj.x, y: obj.y }, arrivalAngle, departureAngle, 'right', obj.shape)
        }
    }
}

function drawMark(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    label?: string,
    color?: string,
    shape: string = 'sphere',
    isSelected?: boolean,
) {
    ctx.save()
    ctx.translate(x, y)
    ctx.beginPath()

    if (isSelected) {
        ctx.strokeStyle = SELECTED_COLOR
    }

    if (shape === 'cylinder') {
        // Square
        ctx.rect(-21, -21, 41, 41)
    } else if (shape === 'pyramid') {
        // Equilateral triangle (approximate)
        ctx.moveTo(0, -25)
        ctx.lineTo(25, 19)
        ctx.lineTo(-25, 19)
        ctx.closePath()
    } else {
        // Sphere (circle)
        ctx.arc(0, 0, 22, 0, Math.PI * 2)
    }

    // Background color
    const originalFillStyle = ctx.fillStyle
    ctx.fillStyle = getColorValue(color || 'white', CourseObjectType.Mark)
    ctx.fill()
    ctx.fillStyle = originalFillStyle

    ctx.stroke()

    if (label) {
        let fontSize = 20
        const text = label

        if (text.length >= 5)
            fontSize = 10
        else if (text.length >= 3)
            fontSize = 15

        ctx.font = `${fontSize}px Roboto, sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'

        ctx.fillStyle = getContrastColor(color || 'white')

        let textY = 0
        if (shape === 'pyramid') {
            textY = 6
        }

        let textX = 0
        if (shape === 'cylinder') {
            textX = -1
        }

        ctx.fillText(text, textX, textY)
    }
    ctx.restore()
}

function drawBoatFlags(
    ctx: CanvasRenderingContext2D,
    colorFlag1: string,
    colorFlag2: string,
    direction: string = 'left',
    direction2?: string,
) {
    const f1 = colorFlag1 && colorFlag1 !== 'none'
        ? colorFlag1
        : (colorFlag2 && colorFlag2 !== 'none' ? colorFlag2 : null)
    const f2 = (f1 === colorFlag1) ? (colorFlag2 && colorFlag2 !== 'none' ? colorFlag2 : null) : null

    if (!f1) return

    const dir1 = direction
    const dir2 = direction2 || direction

    const drawFlag = (color: string, offsetHeight: number, dir: string) => {
        const height = 50
        const base = height / 3

        ctx.save()
        // Tilt 15 degrees from the vertical axis.
        // If direction is left, place on port side (-14.4) and tilt -15 deg.
        // If direction is right, place on starboard side (14.4) and tilt +15 deg.
        const xOffset = dir === 'right' ? 14.4 : -14.4
        const rotation = dir === 'right' ? (15 * Math.PI) / 180 : (-15 * Math.PI) / 180

        ctx.translate(xOffset, 0)
        ctx.rotate(rotation)

        ctx.beginPath()
        ctx.moveTo(0, -offsetHeight)
        ctx.lineTo(base / 2, -height - offsetHeight)
        ctx.lineTo(-base / 2, -height - offsetHeight)
        ctx.closePath()

        ctx.fillStyle = FlagColors.find(c => c.name === color)?.value || '#FFFFFF'
        ctx.fill()
        ctx.strokeStyle = '#000000'
        ctx.lineWidth = 1
        ctx.stroke()
        ctx.restore()
    }

    // If both flags are present
    if (f2) {
        if (dir1 === dir2) {
            // Same side - stacked
            drawFlag(f2, 25, dir2)
            drawFlag(f1, 0, dir1)
        } else {
            // Different sides - both in position 1
            drawFlag(f2, 0, dir2)
            drawFlag(f1, 0, dir1)
        }
    } else {
        drawFlag(f1, 0, dir1)
    }
}

function drawBoat(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    rotation: number,
    label?: string,
    isSelected?: boolean,
    color?: string,
    hideRotationHandle: boolean = false,
    colorFlag1: string = 'none',
    colorFlag2: string = 'none',
    direction: string = 'left',
    direction2?: string,
) {
    ctx.save()
    ctx.translate(x, y)
    ctx.rotate((rotation * Math.PI) / 180)

    if (isSelected && !hideRotationHandle) {
        ctx.save()
        ctx.beginPath()
        ctx.strokeStyle = SELECTED_COLOR
        ctx.setLineDash([2, 2])
        ctx.moveTo(0, -40)
        ctx.lineTo(0, -80)
        ctx.stroke()

        ctx.beginPath()
        ctx.arc(0, -80, 8, 0, Math.PI * 2)
        ctx.fillStyle = SELECTED_COLOR
        ctx.fill()
        ctx.restore()
    }

    if (isSelected) {
        ctx.strokeStyle = SELECTED_COLOR
    }

    ctx.beginPath()
    ctx.moveTo(0, -40)
    ctx.quadraticCurveTo(14.4, -16, 14.4, 0)
    ctx.lineTo(14.4, 24)
    ctx.lineTo(-14.4, 24)
    ctx.lineTo(-14.4, 0)
    ctx.quadraticCurveTo(-14.4, -16, 0, -40)

    // Background color
    const originalFillStyle = ctx.fillStyle
    ctx.fillStyle = getColorValue(color || 'white', CourseObjectType.Boat)
    ctx.fill()
    ctx.fillStyle = originalFillStyle

    ctx.stroke()

    drawBoatFlags(ctx, colorFlag1, colorFlag2, direction, direction2)

    if (label) {
        let fontSize = 20
        const text = label

        if (text.length >= 5)
            fontSize = 10
        else if (text.length >= 3)
            fontSize = 15

        ctx.font = `${fontSize}px Roboto, sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = getContrastColor(color || 'white')
        ctx.fillText(text, 0, 0)
    }

    ctx.restore()
}

function drawObstacle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    x2: number,
    y2: number,
    isSelected?: boolean,
) {
    ctx.save()
    ctx.translate(x, y)

    if (isSelected) {
        ctx.strokeStyle = SELECTED_COLOR
    }

    // Transparent background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.fillRect(0, 0, x2, y2)

    ctx.strokeRect(0, 0, x2, y2)
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(x2, y2)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(x2, 0)
    ctx.lineTo(0, y2)
    ctx.stroke()

    if (isSelected) {
        ctx.strokeStyle = SELECTED_COLOR
        ctx.lineWidth = 2
        // Draw resize handle indication (double corner) - "echo" of the corner
        const s = 25 // size of the corner arms
        const offsets = [10, 17] // distances from the actual corner

        offsets.forEach(offset => {
            ctx.beginPath()
            ctx.moveTo(x2 + offset - s, y2 + offset)
            ctx.lineTo(x2 + offset, y2 + offset)
            ctx.lineTo(x2 + offset, y2 + offset - s)
            ctx.stroke()
        })
    }

    ctx.restore()
}

function drawText(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    rotation: number,
    text: string,
    isSelected?: boolean,
    color?: string,
) {
    ctx.save()
    ctx.translate(x, y)
    ctx.rotate((rotation * Math.PI) / 180)

    if (isSelected) {
        ctx.save()
        ctx.beginPath()
        ctx.strokeStyle = SELECTED_COLOR
        ctx.setLineDash([2, 2])
        ctx.moveTo(0, -20)
        ctx.lineTo(0, -60)
        ctx.stroke()

        ctx.beginPath()
        ctx.arc(0, -60, 8, 0, Math.PI * 2)
        ctx.fillStyle = SELECTED_COLOR
        ctx.fill()
        ctx.restore()
    }

    ctx.font = '28px Roboto, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = isSelected ? SELECTED_COLOR : getColorValue(color || 'white', CourseObjectType.Text)
    ctx.fillText(text || ts('addText'), 0, 0)
    ctx.restore()
}

function drawGate(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    x2: number,
    y2: number,
    label?: string,
    isSelected?: boolean,
    color?: string,
    shape: string = 'sphere',
) {
    const displayLabel = label || ''

    // Dotted line between marks
    ctx.save()
    if (isSelected) {
        ctx.strokeStyle = SELECTED_COLOR
    }
    ctx.lineWidth = 2.8
    ctx.beginPath()
    ctx.setLineDash([4, 4])
    ctx.moveTo(x, y)
    ctx.lineTo(x2, y2)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.restore()

    drawMark(ctx, x, y, displayLabel + 'a', color, shape, isSelected)
    drawMark(ctx, x2, y2, displayLabel + 'b', color, shape, isSelected)
}

function drawRaceLine(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    x2: number,
    y2: number,
    label: string,
    isSelected?: boolean,
    color?: string,
    shape: string = 'sphere',
    x3?: number,
    y3?: number,
    secondLabel?: string,
    secondRaceline?: boolean,
    secondColor?: string,
    secondShape: string = 'sphere',
    thirdColor?: string,
    reversedDirection?: boolean,
    secondReversedDirection?: boolean,
    sharedRaceline?: boolean,
    colorFlag1: string = 'none',
    colorFlag2: string = 'none',
) {
    // Dotted line between mark and boat
    const drawLineAndLabel = (
        bx: number,
        by: number,
        mx: number,
        my: number,
        l: string,
        isReversed?: boolean,
        isSecondArrow?: boolean,
        oppositeSide: boolean = false,
    ) => {
        ctx.save()

        if (isSelected) {
            ctx.strokeStyle = SELECTED_COLOR
        }

        ctx.lineWidth = 2.8
        ctx.beginPath()
        ctx.setLineDash([4, 4])
        ctx.moveTo(bx, by)
        ctx.lineTo(mx, my)
        ctx.stroke()
        ctx.setLineDash([])

        // Direction arrow (perpendicular to the line)
        // Normal arrow at 3/4 (0.75) from boat towards mark
        // Second arrow (if shared) at 1/4 (0.25) from boat towards mark
        const arrowPos = oppositeSide ? 0.25 : 0.75
        const arrowX = bx + (mx - bx) * arrowPos
        const arrowY = by + (my - by) * arrowPos
        const lineAngle = Math.atan2(my - by, mx - bx)
        // Normal direction is "up" (left turn from the line vector boat->mark)
        const arrowAngle = lineAngle + (isReversed ? Math.PI : 0)

        ctx.save()
        ctx.translate(arrowX, arrowY)
        ctx.rotate(arrowAngle)
        ctx.beginPath()
        ctx.lineWidth = 1.4 // Thinner arrow
        ctx.moveTo(0, -45) // 3x longer (was -15 to 15, now -45 to 45)
        ctx.lineTo(0, 45)
        ctx.moveTo(-3.5, 37.5) // Half sized head (was -7 to 7 and 5, now proportional)
        ctx.lineTo(0, 45)
        ctx.lineTo(3.5, 37.5)
        ctx.stroke()

        // Shared label behind the arrow (opposite side of the head)
        if (isSecondArrow) {
            ctx.save() // Inner save for label rotation
            ctx.rotate(-arrowAngle) // Back to canvas rotation 0 (roughly)
            let textAngle = lineAngle

            // For shared labels, we want them to be parallel to the race line
            // but placed behind the arrow.
            // If the arrow is pointing "up", the tail is at the "bottom".

            // Adjust angle to be upright
            if (textAngle > Math.PI / 2) textAngle -= Math.PI
            else if (textAngle < -Math.PI / 2) textAngle += Math.PI

            ctx.rotate(textAngle)
            ctx.font = '18px Roboto, sans-serif'
            ctx.textAlign = 'center'

            // We need to determine if the arrow head is pointing "away" or "towards" the label
            // relative to the line angle.
            // The arrow is at 90 degrees to the line.

            ctx.textBaseline = 'top'
            ctx.fillStyle = isSelected ? SELECTED_COLOR : '#000'
            ctx.fillText(l, 0, 55)
            ctx.restore()
        }

        ctx.restore()

        // Label above the line (only for non-shared)
        if (!isSecondArrow) {
            const midX = (bx + mx) / 2
            const midY = (by + my) / 2
            let angle = Math.atan2(my - by, mx - bx)

            // Ensure text is always "above" the line and upright (angle between -PI/2 and PI/2)
            if (angle > Math.PI / 2) {
                angle -= Math.PI
            } else if (angle < -Math.PI / 2) {
                angle += Math.PI
            }

            ctx.save()
            ctx.translate(midX, midY)
            ctx.rotate(angle)
            ctx.font = '18px Roboto, sans-serif'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'bottom'
            ctx.fillStyle = isSelected ? SELECTED_COLOR : '#000'
            ctx.fillText(l, 0, -5)
            ctx.restore()
        }
        ctx.restore()
    }

    drawLineAndLabel(x, y, x2, y2, label, reversedDirection, sharedRaceline)

    if (sharedRaceline) {
        drawLineAndLabel(x, y, x2, y2, secondLabel || '', secondReversedDirection, true, true)
    } else if (secondRaceline) {
        drawLineAndLabel(x, y, x3, y3, secondLabel || '', secondReversedDirection, false)
    }

    // Boat rotation
    let rotation = 0 // Default for two lines or wind direction
    if (!secondRaceline) {
        // Single line: rotate boat perpendicular to the line and pointing more "upwards" (against wind)
        const angle = Math.atan2(y2 - y, x2 - x)
        // Two possible perpendicular angles: angle - PI/2 or angle + PI/2
        // We want the longitudinal axis of the boat to be perpendicular to the line.
        // The boat's 0 deg rotation is bow at (0, -40).
        // To be perpendicular to the line (angle), the boat rotation should be angle + 0?
        // No, if the boat is at 0 deg, it points UP. If the line is 0 deg (horizontal to the right), the boat is perpendicular.
        // So rotation = angle.
        const rot1 = angle
        const rot2 = angle + Math.PI

        // We want the one where sin(rot) is most negative (pointing up)
        // If sin is equal (horizontal line), we prefer pointing up (negative sin is not possible, both are 0)
        // In case of horizontal line, rot1=0 (right), rot2=PI (left). Both sin are 0.
        // The previous logic would pick rot1 if sin(rot1) < sin(rot2) is false.
        // If angle is 0 (x2 > x), rot1 = 0, rot2 = PI. sin(0)=0, sin(PI)=0. 0 < 0 is false, so it picks rot2 (PI).
        // PI means the boat points DOWN. We want it to point UP.
        // So if they are equal, we should check if we want to force it.
        if (Math.abs(Math.sin(rot1) - Math.sin(rot2)) < 0.0001) {
            // Horizontal line: angle is 0 or PI.
            // We want boat to point UP, which is 0 degrees in our system (but wait, rotation 0 IS up).
            // Our model's 0 deg is UP. So if rotation is 0, it points up.
            // If the line is horizontal, angle is 0 or PI.
            // If angle is 0, rot1 = 0, rot2 = PI. rot1 (0) points UP, rot2 (PI) points DOWN.
            // If angle is PI, rot1 = PI, rot2 = 2PI (0). rot2 (0) points UP, rot1 (PI) points DOWN.
            rotation = 0
        } else {
            rotation = (Math.sin(rot1) < Math.sin(rot2) ? rot1 : rot2) * 180 / Math.PI
        }
    }

    // Center/Left: Boat (no label)
    let dir1 = 'left'
    let dir2 = 'left'

    const getSide = (mx: number, my: number) => {
        // Line vector from boat to mark
        const lineX = mx - x
        const lineY = my - y
        const lineAngle = Math.atan2(lineY, lineX)

        // Boat rotation angle in radians
        const boatAngle = (rotation * Math.PI) / 180 - Math.PI / 2

        // Relative angle
        let relativeAngle = lineAngle - boatAngle
        while (relativeAngle > Math.PI) relativeAngle -= 2 * Math.PI
        while (relativeAngle < -Math.PI) relativeAngle += 2 * Math.PI

        // If relative angle is positive, it's to the right of the boat's longitudinal axis (starboard)
        // because Y is down and we are looking from above.
        // Wait, let's re-verify.
        // Boat points up (rotation 0). longitudinal axis is (0, -1).
        // If mark is at (1, 0), line vector is (1, 0). lineAngle = 0.
        // boatAngle = -PI/2.
        // relativeAngle = 0 - (-PI/2) = PI/2 (90 deg).
        // Positive PI/2 means it's to the right (starboard).
        return relativeAngle > 0 ? 'right' : 'left'
    }

    dir1 = getSide(x2, y2)
    if (secondRaceline && x3 !== undefined && y3 !== undefined && !sharedRaceline) {
        dir2 = getSide(x3, y3)
    } else {
        dir2 = dir1
    }

    drawBoat(ctx, x, y, rotation, '', isSelected, thirdColor || color, true, colorFlag1, colorFlag2, dir1, dir2)

    // Right side: Mark (no label)
    drawMark(ctx, x2, y2, '', color, shape, isSelected)

    // Second side: Mark (no label)
    if (secondRaceline && x3 !== undefined && y3 !== undefined) {
        drawMark(ctx, x3, y3, '', secondColor, secondShape, isSelected)
    }
}

function drawCanvasAndGrid(ctx: CanvasRenderingContext2D, width: number, height: number) {
    ctx.fillStyle = '#fafafa'
    ctx.fillRect(0, 0, width, height)

    // Border
    ctx.strokeStyle = '#bbb'
    ctx.setLineDash([5, 5])
    ctx.lineWidth = 1
    ctx.strokeRect(10, 10, width - 20, height - 20)

    // Grid of dots
    ctx.fillStyle = '#bbb'
    ctx.setLineDash([])

    const spacing = 50

    for (let x = spacing; x < width; x += spacing) {
        for (let y = spacing; y < height; y += spacing) {
            ctx.beginPath()
            ctx.arc(x, y, 1, 0, Math.PI * 2)
            ctx.fill()
        }
    }
}

function updateCanvasSize() {
    const canvas = canvasRef.value
    if (!canvas) return

    const container = canvas.parentElement
    if (!container) return

    // To get the actual available space without being stretched by the canvas itself,
    // we can temporarily set the canvas size to 0 or use a different approach.
    // However, since we are in a flex column or absolute container,
    // container.clientWidth/Height should be reliable if the container has its own overflow.

    // We'll use a trick: save current size, set to 1x1, measure container, restore/update.
    const oldW = canvas.style.width
    const oldH = canvas.style.height
    const oldDisp = canvas.style.display
    canvas.style.display = 'none'

    const minWidth = container.clientWidth
    const minHeight = container.clientHeight

    canvas.style.display = oldDisp
    canvas.style.width = oldW
    canvas.style.height = oldH

    // Calculate actual bounds of all objects
    let realMinX = Infinity
    let realMinY = Infinity
    let realMaxX = -Infinity
    let realMaxY = -Infinity

    if (objects.value.length === 0) {
        canvasWidth.value = minWidth
        canvasHeight.value = minHeight
        return
    }

    const checkBounds = (x?: number, y?: number) => {
        // We only care about coordinates that are not exactly 0,
        // because unused coordinates (like x2 for a Mark) are stored as 0 in DB.
        // Valid coordinates can be 0, but they should trigger a shift if realMinX < 100 anyway.
        // However, we don't want unused 0s to pull realMinX to 0.
        // We also check obj.x/y explicitly in the loop below to ensure at least primary position is counted even if 0.
        if (x !== undefined && x !== 0) {
            realMinX = Math.min(realMinX, x)
            realMaxX = Math.max(realMaxX, x)
        }
        if (y !== undefined && y !== 0) {
            realMinY = Math.min(realMinY, y)
            realMaxY = Math.max(realMaxY, y)
        }
    }

    objects.value.forEach(obj => {
        // Always count primary position, even if it is 0
        realMinX = Math.min(realMinX, obj.x)
        realMaxX = Math.max(realMaxX, obj.x)
        realMinY = Math.min(realMinY, obj.y)
        realMaxY = Math.max(realMaxY, obj.y)

        // Only check extra coordinates for types that actually use them for positioning/size
        if (
            obj.type === CourseObjectType.Gate || obj.type === CourseObjectType.RaceLine
            || obj.type === CourseObjectType.Obstacle
        ) {
            checkBounds(obj.x2, obj.y2)
            checkBounds(obj.x3, obj.y3)
        }
    })

    if (isDragging.value) {
        const obj = objects.value.find(o => o.id === draggedObjectId.value)
        if (obj) {
            checkBounds(draggedObjectPosition.value.x, draggedObjectPosition.value.y)
            // If dragging an obstacle corner or gate point, we might need to check bounds too,
            // but those are usually handled by updating obj.x2/y2 etc. which are checked above.
        }
    }

    // If no objects (excluding wind/dragging), use container size
    if (realMinX === Infinity) {
        // This case should be covered by objects.value.length === 0 check above,
        // but just in case some objects exist but have no valid bounds.
        canvasWidth.value = minWidth
        canvasHeight.value = minHeight
        return
    }

    // We want exactly 100px space around objects (excluding wind)
    // If realMinX < 100, we need to shift right
    // If realMinX > 100, we need to shift left (shrink)
    const margin = 100
    const offsetX = margin - realMinX
    const offsetY = margin - realMinY

    if (Math.abs(offsetX) > 1 || Math.abs(offsetY) > 1) {
        // Shift all objects in DB to maintain exactly 100px margin from top-left
        objectsRepo.save(objects.value.map(obj => ({
            id: obj.id,
            x: obj.x + offsetX,
            y: obj.y + offsetY,
            x2: obj.x2 !== undefined ? obj.x2 + offsetX : undefined,
            y2: obj.y2 !== undefined ? obj.y2 + offsetY : undefined,
            x3: obj.x3 !== undefined ? obj.x3 + offsetX : undefined,
            y3: obj.y3 !== undefined ? obj.y3 + offsetY : undefined,
        })))

        if (isDragging.value) {
            draggedObjectPosition.value.x += offsetX
            draggedObjectPosition.value.y += offsetY
            dragOffset.value.x += offsetX
            dragOffset.value.y += offsetY
            dragStartPos.value.x += offsetX
            dragStartPos.value.y += offsetY
        }

        if (contextMenuVisible.value) {
            contextMenuPosition.value.x += offsetX
            contextMenuPosition.value.y += offsetY
        }

        const parent = canvas.parentElement
        if (parent) {
            parent.scrollLeft += offsetX
            parent.scrollTop += offsetY
        }

        // Update real bounds after shift
        realMaxX += offsetX
        realMaxY += offsetY
    }

    // Now set canvas size to (realMaxX + 100)
    // For width: if all objects are within viewport, set to viewport width.
    // Otherwise set to (realMaxX + 100).
    // The user said: "Na délku bych zkracoval na délku obrazovky, pokud nejsou objekty níže, příp. 100px pod nejnižší objekt."
    // We keep minWidth/minHeight as the baseline to avoid the canvas being smaller than the visible area.
    const targetWidth = Math.max(minWidth, (realMaxX + margin) * zoom.value)
    const targetHeight = Math.max(minHeight, (realMaxY + margin) * zoom.value)

    canvasWidth.value = Math.floor(targetWidth)
    canvasHeight.value = Math.floor(targetHeight)
}

function draw() {
    const canvas = canvasRef.value
    if (!canvas) return

    updateCanvasSize()

    // Use current zoom to ensure we don't have mismatch
    const currentZoom = zoom.value

    canvas.width = canvasWidth.value
    canvas.height = canvasHeight.value
    canvas.style.width = `${canvasWidth.value}px`
    canvas.style.height = `${canvasHeight.value}px`

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.save()
    ctx.scale(currentZoom, currentZoom)

    drawCanvasAndGrid(ctx, canvas.width / currentZoom, canvas.height / currentZoom)

    // Draw objects
    objects.value.forEach(obj => {
        ctx.strokeStyle = '#000'
        ctx.fillStyle = '#000'
        ctx.lineWidth = 2.8

        // Highlight dragged or rotated object
        let x = obj.x
        let y = obj.y
        let x2 = obj.x2
        let y2 = obj.y2
        let x3 = obj.x3
        let y3 = obj.y3
        let rotation = obj.rotation

        if ((isDragging.value && draggedObjectId.value === obj.id) || selectedObjectId.value === obj.id) {
            ctx.strokeStyle = SELECTED_COLOR
            ctx.fillStyle = SELECTED_COLOR

            if (isDragging.value && draggedObjectId.value === obj.id) {
                if (obj.type === CourseObjectType.Obstacle && dragTarget.value === 'primary') {
                    x = draggedObjectPosition.value.x
                    y = draggedObjectPosition.value.y
                } else if (obj.type === CourseObjectType.Obstacle && dragTarget.value === 'secondary') {
                    x2 = draggedObjectPosition.value.x
                    y2 = draggedObjectPosition.value.y
                } else if (dragTarget.value === 'primary') {
                    const dx = draggedObjectPosition.value.x - obj.x
                    const dy = draggedObjectPosition.value.y - obj.y

                    x = draggedObjectPosition.value.x
                    y = draggedObjectPosition.value.y
                    x2 = obj.x2 + dx
                    y2 = obj.y2 + dy
                    x3 = obj.x3 + dx
                    y3 = obj.y3 + dy
                } else if (dragTarget.value === 'secondary') {
                    x2 = draggedObjectPosition.value.x
                    y2 = draggedObjectPosition.value.y
                } else if (dragTarget.value === 'tertiary') {
                    x3 = draggedObjectPosition.value.x
                    y3 = draggedObjectPosition.value.y
                }
            }
        }

        if (isRotating.value && selectedObjectId.value === obj.id) {
            rotation = rotatingObjectRotation.value
        }

        const isSelected = selectedObjectId.value === obj.id

        // Create a temporary object with actual (possibly dragged) coordinates for drawing helpers
        const activeObj = { ...obj, x, y, x2, y2, x3, y3, rotation }

        switch (obj.type) {
            case CourseObjectType.Mark:
                drawDirection(ctx, activeObj)
                drawMark(ctx, x, y, obj.label || 'M', obj.color, obj.shape, isSelected)
                break
            case CourseObjectType.Gate:
                drawDirection(ctx, activeObj)
                drawGate(ctx, x, y, x2, y2, obj.label, isSelected, obj.color, obj.shape)
                break
            case CourseObjectType.RaceLine: {
                const activeObj = { ...obj, x, y, x2, y2, x3, y3, rotation }
                drawDirection(ctx, activeObj)
                drawRaceLine(
                    ctx,
                    x,
                    y,
                    x2,
                    y2,
                    obj.label,
                    isSelected,
                    obj.color,
                    obj.shape,
                    x3,
                    y3,
                    obj.secondLabel,
                    obj.secondRaceline,
                    obj.secondColor,
                    obj.secondShape,
                    obj.thirdColor,
                    obj.reversedDirection,
                    obj.secondReversedDirection,
                    obj.sharedRaceline,
                    obj.colorFlag1,
                    obj.colorFlag2,
                )
                break
            }
            case CourseObjectType.Boat:
                drawDirection(ctx, activeObj)
                drawBoat(
                    ctx,
                    x,
                    y,
                    rotation,
                    obj.label,
                    isSelected,
                    obj.color,
                    false,
                    obj.colorFlag1,
                    obj.colorFlag2,
                    obj.direction,
                )
                break
            case CourseObjectType.Obstacle:
                drawObstacle(ctx, x, y, x2, y2, isSelected)
                break
            case CourseObjectType.Wind:
                drawWind(ctx, x, y, isSelected)
                break
            case CourseObjectType.Text:
                drawText(ctx, x, y, rotation, obj.label, isSelected, obj.color)
                break
        }
    })
    ctx.restore()
}

function handleMouseDown(event: MouseEvent) {
    const canvas = canvasRef.value
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = (event.clientX - rect.left) / zoom.value
    const y = (event.clientY - rect.top) / zoom.value

    // Check if we clicked on a rotation handle first
    if (selectedObjectId.value) {
        const obj = objects.value.find(o => o.id === selectedObjectId.value)

        if (obj && obj.type !== CourseObjectType.Obstacle) {
            const rad = (obj.rotation * Math.PI) / 180
            const handleDist = (obj.type === CourseObjectType.Boat) ? 80 : 60
            const handleX = obj.x + Math.sin(rad) * handleDist
            const handleY = obj.y - Math.cos(rad) * handleDist

            const dx = x - handleX
            const dy = y - handleY

            if (Math.sqrt(dx * dx + dy * dy) < 15) {
                isRotating.value = true
                rotatingObjectRotation.value = obj.rotation
                return
            }
        }
    }

    // Find object under cursor (reverse order to pick the top one)
    const reversedObjects = [...objects.value].reverse()
    let clickTarget: 'primary' | 'secondary' | 'tertiary' = 'primary'

    const found = reversedObjects.find(obj => {
        const objX = (isDragging.value && draggedObjectId.value === obj.id) ? draggedObjectPosition.value.x : obj.x
        const objY = (isDragging.value && draggedObjectId.value === obj.id) ? draggedObjectPosition.value.y : obj.y

        // For Gate, RaceLine, we check marks (or boat/mark) and the line between them
        if (obj.type === CourseObjectType.Gate || obj.type === CourseObjectType.RaceLine) {
            // Check right mark first (to allow moving it independently)
            const objX2 = (isDragging.value && draggedObjectId.value === obj.id && dragTarget.value === 'secondary')
                ? draggedObjectPosition.value.x
                : obj.x2
            const objY2 = (isDragging.value && draggedObjectId.value === obj.id && dragTarget.value === 'secondary')
                ? draggedObjectPosition.value.y
                : obj.y2
            const dx2 = x - objX2
            const dy2 = y - objY2

            if (Math.sqrt(dx2 * dx2 + dy2 * dy2) < 25) {
                clickTarget = 'secondary'
                return true
            }

            // Check third mark for RaceLine
            if (obj.type === CourseObjectType.RaceLine && (obj.secondRaceline || obj.sharedRaceline)) {
                const objX3 = (isDragging.value && draggedObjectId.value === obj.id && dragTarget.value === 'tertiary')
                    ? draggedObjectPosition.value.x
                    : obj.x3
                const objY3 = (isDragging.value && draggedObjectId.value === obj.id && dragTarget.value === 'tertiary')
                    ? draggedObjectPosition.value.y
                    : obj.y3
                const dx3 = x - objX3
                const dy3 = y - objY3

                if (Math.sqrt(dx3 * dx3 + dy3 * dy3) < 25) {
                    clickTarget = 'tertiary'
                    return true
                }
            }

            // Check left side (at objX, objY)
            const dx1 = x - objX
            const dy1 = y - objY
            const dist1 = Math.sqrt(dx1 * dx1 + dy1 * dy1)

            // For RaceLine, left side is a boat, which has higher tolerance
            let leftTolerance = 25

            if (obj.type === CourseObjectType.RaceLine)
                leftTolerance = 40

            if (dist1 < leftTolerance) {
                clickTarget = 'primary'
                return true
            }

            // Check middle of first line
            const midX = (objX + objX2) / 2
            const midY = (objY + objY2) / 2
            const dmx = x - midX
            const dmy = y - midY

            if (Math.sqrt(dmx * dmx + dmy * dmy) < 20) {
                clickTarget = 'primary'
                return true
            }

            // Check middle of second line
            if (obj.type === CourseObjectType.RaceLine && (obj.secondRaceline || obj.sharedRaceline)) {
                const midX2 = (objX + obj.x3) / 2
                const midY2 = (objY + obj.y3) / 2
                const dmx2 = x - midX2
                const dmy2 = y - midY2

                if (Math.sqrt(dmx2 * dmx2 + dmy2 * dmy2) < 20) {
                    clickTarget = 'primary'
                    return true
                }
            }

            return false
        }

        // For Obstacle, check primary (top-left) and secondary (bottom-right resizing handle)
        if (obj.type === CourseObjectType.Obstacle) {
            // Secondary: bottom-right corner [x2, y2] relative to [x, y] - handle is now outside (echo of the corner)
            const cornerX = objX + obj.x2
            const cornerY = objY + obj.y2

            // Check if click is within the outer resize handle (echo zone)
            // Handle offsets are [15, 30] and size is 25.
            // So the farthest point is roughly corner + 30.
            if (x >= cornerX - 5 && x <= cornerX + 45 && y >= cornerY - 5 && y <= cornerY + 45) {
                clickTarget = 'secondary'
                return true
            }

            // Primary: check if point is inside the rectangle
            if (x >= objX - 10 && x <= objX + obj.x2 + 10 && y >= objY - 10 && y <= objY + obj.y2 + 10) {
                clickTarget = 'primary'
                return true
            }

            return false
        }

        // For Wind, check the arrow area
        if (obj.type === CourseObjectType.Wind) {
            // Arrow is from [objX - 30, objY] to [objX + 30, objY + 128] roughly
            // Plus label area to the right
            if (x >= objX - 40 && x <= objX + 110 && y >= objY - 10 && y <= objY + 140) {
                clickTarget = 'primary'
                return true
            }
            return false
        }

        const dx = x - objX
        const dy = y - objY
        const dist = Math.sqrt(dx * dx + dy * dy)

        // Tolerance based on object type
        let tolerance = 25

        if (obj.type === CourseObjectType.Text) tolerance = 40
        if (obj.type === CourseObjectType.Boat) tolerance = 40
        if (obj.type === CourseObjectType.Wind) tolerance = 50 // Keep as fallback

        clickTarget = 'primary'

        return dist < tolerance
    })

    if (found) {
        selectedObjectId.value = found.id
        isDragging.value = true
        draggedObjectId.value = found.id
        dragTarget.value = clickTarget

        const startX = (clickTarget === 'primary' || found.type === CourseObjectType.Obstacle)
            ? found.x
            : (clickTarget === 'secondary' ? found.x2 : found.x3)
        const startY = (clickTarget === 'primary' || found.type === CourseObjectType.Obstacle)
            ? found.y
            : (clickTarget === 'secondary' ? found.y2 : found.y3)

        if (found.type === CourseObjectType.Obstacle && clickTarget === 'secondary') {
            dragOffset.value = {
                x: x - (found.x + found.x2),
                y: y - (found.y + found.y2),
            }
            dragStartPos.value = { x: found.x2, y: found.y2 }
        } else if (found.type === CourseObjectType.Obstacle && clickTarget === 'primary') {
            dragOffset.value = {
                x: x - found.x,
                y: y - found.y,
            }
            dragStartPos.value = { x: found.x, y: found.y }
        } else {
            dragOffset.value = {
                x: x - startX,
                y: y - startY,
            }
            dragStartPos.value = { x: startX, y: startY }
        }
        draggedObjectPosition.value = {
            x: startX,
            y: startY,
        }
        draw()
    } else {
        selectedObjectId.value = null
        draw()
    }
}

function handleMouseMove(event: MouseEvent) {
    const canvas = canvasRef.value
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = (event.clientX - rect.left) / zoom.value
    const y = (event.clientY - rect.top) / zoom.value

    if (isRotating.value && selectedObjectId.value) {
        const obj = objects.value.find(o => o.id === selectedObjectId.value)

        if (obj) {
            // Keep rotation handle logic free but maybe we should constrain it if it goes out of canvas?
            // Actually, rotating usually doesn't move the center, and the handle is just for UI.
            // But if the handle goes out of canvas, the user can't grab it easily.
            // However, the issue only mentions Drag and Drop and Resize.
            const dx = x - obj.x
            const dy = y - obj.y

            // Calculate angle in degrees
            // atan2 returns angle in radians from positive X axis clockwise
            // Our 0 is North (negative Y), so we need to adjust
            let angleRad = Math.atan2(dy, dx)
            let angle = angleRad * 180 / Math.PI
            angle += 90 // North is now 0
            if (angle < 0) angle += 360

            if (event.shiftKey) {
                // Snap to 45 degrees
                angle = Math.round(angle / 45) * 45

                if (angle >= 360)
                    angle -= 360
            }

            rotatingObjectRotation.value = angle

            draw()
        }
        return
    }

    if (!isDragging.value || !draggedObjectId.value) return

    const obj = objects.value.find(o => o.id === draggedObjectId.value)
    if (!obj) return

    // Update local temporary position for smooth dragging
    if (obj.type === CourseObjectType.Obstacle && dragTarget.value === 'secondary') {
        const mouseX = x - dragOffset.value.x
        const mouseY = y - dragOffset.value.y

        // mouseX/mouseY here are absolute target coordinates for the corner
        // Calculate new relative sizes
        let newX2 = mouseX - obj.x
        let newY2 = mouseY - obj.y

        if (event.shiftKey) {
            const startX2 = dragStartPos.value.x // In handleMouseDown we saved obj.x2/y2 for Obstacle secondary
            const startY2 = dragStartPos.value.y
            const dx = Math.abs(newX2 - startX2)
            const dy = Math.abs(newY2 - startY2)

            // Orthogonal (X or Y) change relative to start of drag
            if (dx > dy) {
                newY2 = startY2
            } else {
                newX2 = startX2
            }
        }

        // Boundary check for resizing: obj.x + newX2 must be <= canvas.width
        // Actually, with auto-expansion, we don't need these strict boundary checks
        // but we might want to keep them to avoid runaway expansion while dragging?
        // Let's remove them for now to allow free movement.

        // Limit movement to max 300px beyond visible viewport
        const container = canvas.parentElement
        if (container) {
            const viewportLeft = container.scrollLeft
            const viewportRight = container.scrollLeft + container.clientWidth
            const viewportTop = container.scrollTop
            const viewportBottom = container.scrollTop + container.clientHeight

            // newX2/newY2 are relative to obj.x/obj.y for Obstacles
            // We want to limit the absolute corner position: (obj.x + newX2)
            const absoluteX2 = obj.x + newX2
            const absoluteY2 = obj.y + newY2

            const limitedX2 = Math.max(viewportLeft - 300, Math.min(viewportRight + 300, absoluteX2))
            const limitedY2 = Math.max(viewportTop - 300, Math.min(viewportBottom + 300, absoluteY2))

            newX2 = limitedX2 - obj.x
            newY2 = limitedY2 - obj.y
        }

        // Limit minimum size to 20px
        draggedObjectPosition.value = {
            x: Math.max(20, newX2),
            y: Math.max(20, newY2),
        }
    } else {
        let targetX = x - dragOffset.value.x
        let targetY = y - dragOffset.value.y

        if (event.shiftKey) {
            const startX = dragStartPos.value.x
            const startY = dragStartPos.value.y

            if (dragTarget.value === 'secondary' || dragTarget.value === 'tertiary') {
                // For Gate and RaceLine secondary/tertiary points, snap to 45 degree increments relative to primary point
                if (obj.type === CourseObjectType.Gate || obj.type === CourseObjectType.RaceLine) {
                    const dx = targetX - obj.x
                    const dy = targetY - obj.y
                    const dist = Math.sqrt(dx * dx + dy * dy)
                    let angleRad = Math.atan2(dy, dx)
                    let angleDeg = angleRad * 180 / Math.PI

                    // Snap to 45 degrees
                    angleDeg = Math.round(angleDeg / 45) * 45
                    angleRad = angleDeg * Math.PI / 180

                    targetX = obj.x + dist * Math.cos(angleRad)
                    targetY = obj.y + dist * Math.sin(angleRad)
                } else {
                    // Fallback to orthogonal for other secondary targets (if any)
                    const dx = Math.abs(targetX - startX)
                    const dy = Math.abs(targetY - startY)
                    if (dx > dy) targetY = startY
                    else targetX = startX
                }
            } else {
                // Orthogonal (X or Y) for primary point (moving whole object)
                const dx = Math.abs(targetX - startX)
                const dy = Math.abs(targetY - startY)

                if (dx > dy) {
                    // Mostly horizontal
                    targetY = startY
                } else {
                    // Mostly vertical
                    targetX = startX
                }
            }
        }

        // Boundary check for primary point or secondary point
        // With auto-expansion, we allow free movement but maybe limit minimums?
        if (dragTarget.value === 'primary') {
            // Avoid negative coordinates only if we don't want to expand left/up infinitely without objects
            // But our updateCanvasSize handles expansion, so let's allow it.
            // We just need to make sure we don't fly off too far.
        }

        // Limit movement to max 300px beyond visible viewport
        const container = canvas.parentElement
        if (container) {
            const viewportLeft = container.scrollLeft
            const viewportRight = container.scrollLeft + container.clientWidth
            const viewportTop = container.scrollTop
            const viewportBottom = container.scrollTop + container.clientHeight

            // Limit target coordinates to viewport + 300px margin
            // Also prevent going too far into negative to avoid extreme coordinate shifts,
            // but updateCanvasSize handles negative expansion, so we just limit it to 300px beyond visible.
            targetX = Math.max(viewportLeft - 300, Math.min(viewportRight + 300, targetX))
            targetY = Math.max(viewportTop - 300, Math.min(viewportBottom + 300, targetY))
        }

        draggedObjectPosition.value = {
            x: targetX,
            y: targetY,
        }
    }
    draw()
}

function handleMouseUp() {
    if (isRotating.value && selectedObjectId.value) {
        objectsRepo.save({
            id: selectedObjectId.value,
            rotation: rotatingObjectRotation.value,
        })

        const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
        if (event) {
            event.setModified()
        }

        isRotating.value = false
        // We keep selectedObjectId so the handle stays visible until clicked elsewhere
        draw()
        return
    }

    if (isDragging.value && draggedObjectId.value) {
        const obj = objectsRepo.find(draggedObjectId.value)
        if (obj) {
            if (obj.type === CourseObjectType.Obstacle && dragTarget.value === 'secondary') {
                objectsRepo.save({
                    id: draggedObjectId.value,
                    x2: Math.max(20, draggedObjectPosition.value.x),
                    y2: Math.max(20, draggedObjectPosition.value.y),
                })
            } else if (dragTarget.value === 'primary') {
                const dx = draggedObjectPosition.value.x - obj.x
                const dy = draggedObjectPosition.value.y - obj.y

                // Save the final position to the repository
                const savePayload: Partial<EventCourseObject> = {
                    id: draggedObjectId.value,
                    x: draggedObjectPosition.value.x,
                    y: draggedObjectPosition.value.y,
                }

                // For objects with relative secondary points (Gate, RaceLine), update them too
                if (obj.type === CourseObjectType.Gate || obj.type === CourseObjectType.RaceLine) {
                    savePayload.x2 = obj.x2 + dx
                    savePayload.y2 = obj.y2 + dy
                    if (obj.type === CourseObjectType.RaceLine && (obj.secondRaceline || obj.sharedRaceline)) {
                        savePayload.x3 = obj.x3 + dx
                        savePayload.y3 = obj.y3 + dy
                    }
                }

                objectsRepo.save(savePayload)
            } else if (dragTarget.value === 'secondary') {
                // Save the second mark final position to the repository
                objectsRepo.save({
                    id: draggedObjectId.value,
                    x2: draggedObjectPosition.value.x,
                    y2: draggedObjectPosition.value.y,
                })
            } else if (dragTarget.value === 'tertiary') {
                // Save the third mark final position to the repository
                objectsRepo.save({
                    id: draggedObjectId.value,
                    x3: draggedObjectPosition.value.x,
                    y3: draggedObjectPosition.value.y,
                })
            }
        }

        const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
        if (event) {
            event.setModified()
        }
    }

    isDragging.value = false
    draggedObjectId.value = null
    draw()
}

function toggleSecondRaceLine(obj: EventCourseObject) {
    const newVal = !obj.secondRaceline
    const payload: Partial<EventCourseObject> = {
        id: obj.id,
        secondRaceline: newVal,
    }

    if (newVal) {
        // If enabling, set x3, y3 to opposite side of x2, y2 relative to x, y
        payload.x3 = obj.x - (obj.x2 - obj.x)
        payload.y3 = obj.y - (obj.y2 - obj.y)

        // Initialize second color and shape from primary ones
        payload.secondColor = obj.secondColor || obj.color
        payload.secondShape = obj.secondShape || obj.shape

        payload.sharedRaceline = false
        payload.secondReversedDirection = !obj.reversedDirection

        // If second line is being added and first line has orange flag, set second to blue
        if (obj.colorFlag1 === 'orange' && obj.colorFlag2 === 'none') {
            payload.colorFlag2 = 'blue'
        }
    }

    objectsRepo.save(payload)

    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function toggleSharedRaceLine(obj: EventCourseObject) {
    const newVal = !obj.sharedRaceline
    const payload: Partial<EventCourseObject> = {
        id: obj.id,
        sharedRaceline: newVal,
    }

    if (newVal) {
        // Shared line means x3, y3 is used as a second direction, initially opposite to first
        payload.x3 = obj.x - (obj.x2 - obj.x)
        payload.y3 = obj.y - (obj.y2 - obj.y)
        payload.secondRaceline = false
        payload.secondReversedDirection = !obj.reversedDirection

        // Use default labels for shared if not set
        if (!obj.secondLabel) {
            const hasFinish = objects.value.some(o =>
                (o.label?.toUpperCase() === ts('finish').toUpperCase())
                || (o.type === CourseObjectType.RaceLine && o.secondLabel?.toUpperCase() === ts('finish').toUpperCase())
            )

            if (!hasFinish) payload.secondLabel = ts('finish')
            else payload.secondLabel = ts('line')
        }
    }

    objectsRepo.save(payload)

    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function separateRaceLines(obj: EventCourseObject) {
    objectsRepo.save({
        id: obj.id,
        sharedRaceline: false,
        secondRaceline: true,
        secondReversedDirection: !obj.reversedDirection,
    })

    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function resetWindPosition(obj: EventCourseObject) {
    objectsRepo.save({
        id: obj.id,
        x: canvasRef.value ? (canvasRef.value.width * 3) / 4 : 50,
        y: 50,
    })
    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    draw()
}

function resetRotation(obj: EventCourseObject) {
    objectsRepo.save({
        id: obj.id,
        rotation: 0,
    })
    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    draw()
}

function changeColor(obj: EventCourseObject, color: string, part: 'primary' | 'secondary' | 'third' = 'primary') {
    const savePayload: Partial<EventCourseObject> = { id: obj.id }
    if (part === 'secondary') savePayload.secondColor = color
    else if (part === 'third') savePayload.thirdColor = color
    else savePayload.color = color

    objectsRepo.save(savePayload)
    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function changeShape(obj: EventCourseObject, shape: string, isSecond: boolean = false) {
    const savePayload: Partial<EventCourseObject> = { id: obj.id }
    if (isSecond) savePayload.secondShape = shape
    else savePayload.shape = shape

    objectsRepo.save(savePayload)
    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function toggleDirection(obj: EventCourseObject, isSecond: boolean = false) {
    const savePayload: Partial<EventCourseObject> = { id: obj.id }
    if (isSecond) savePayload.secondReversedDirection = !obj.secondReversedDirection
    else savePayload.reversedDirection = !obj.reversedDirection

    objectsRepo.save(savePayload)
    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function toggleShowDirection(obj: EventCourseObject) {
    objectsRepo.save({ id: obj.id, showDirection: !obj.showDirection })
    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function setDirection(obj: EventCourseObject, direction: string) {
    objectsRepo.save({ id: obj.id, direction })
    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function changeFlagColor(obj: EventCourseObject, color: string, part: 1 | 2) {
    const savePayload: Partial<EventCourseObject> = { id: obj.id }
    if (part === 1) savePayload.colorFlag1 = color
    else savePayload.colorFlag2 = color

    objectsRepo.save(savePayload)
    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function setNextObject(obj: EventCourseObject, nextId: string | null, isSecondary: boolean = false) {
    const oldNextId = obj.nextObjectId

    // If there was a previous next object, clear its prev pointer if it points to us
    if (oldNextId) {
        const oldNext = objectsRepo.find(oldNextId)
        if (oldNext && oldNext.type !== CourseObjectType.RaceLine && oldNext.prevObjectId === obj.id) {
            objectsRepo.save({ id: oldNext.id, prevObjectId: null, prevObjectIdSecondary: false })
        }
    }

    // Save the new next object for the current object
    objectsRepo.save({ id: obj.id, nextObjectId: nextId, nextObjectIdSecondary: isSecondary })

    // If setting a new next object, set its prev pointer to us (unless it's a RaceLine)
    if (nextId) {
        const nextObj = objectsRepo.find(nextId)
        if (nextObj && nextObj.type !== CourseObjectType.RaceLine) {
            objectsRepo.save({ id: nextObj.id, prevObjectId: obj.id, prevObjectIdSecondary: false })
        }
    }

    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function setPrevObject(obj: EventCourseObject, prevId: string | null, isSecondary: boolean = false) {
    const oldPrevId = obj.prevObjectId

    // If there was a previous prev object, clear its next pointer if it points to us
    if (oldPrevId) {
        const oldPrev = objectsRepo.find(oldPrevId)
        if (oldPrev && oldPrev.type !== CourseObjectType.RaceLine && oldPrev.nextObjectId === obj.id) {
            objectsRepo.save({ id: oldPrev.id, nextObjectId: null, nextObjectIdSecondary: false })
        }
    }

    // Save the new prev object for the current object
    objectsRepo.save({ id: obj.id, prevObjectId: prevId, prevObjectIdSecondary: isSecondary })

    // If setting a new prev object, set its next pointer to us (unless it's a RaceLine)
    if (prevId) {
        const prevObj = objectsRepo.find(prevId)
        if (prevObj && prevObj.type !== CourseObjectType.RaceLine) {
            objectsRepo.save({ id: prevObj.id, nextObjectId: obj.id, nextObjectIdSecondary: false })
        }
    }

    const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
    if (event) {
        event.setModified()
    }
    contextMenuVisible.value = false
    draw()
}

function getObjectName(obj: EventCourseObject): string {
    switch (obj.type) {
        case CourseObjectType.Mark:
            return `${ts('objectMark')} ${obj.label}`
        case CourseObjectType.Gate:
            return `${ts('objectGate')} ${obj.label}a-${obj.label}b`
        case CourseObjectType.Boat:
            return `${ts('objectBoat')} ${obj.label}`
        case CourseObjectType.RaceLine:
            if (
                obj.label.toUpperCase().includes(ts('start').toUpperCase())
                && obj.label.toUpperCase().includes(ts('finish').toUpperCase())
            ) {
                return ts('objectRaceLine')
            }
            if (obj.label.toUpperCase().includes(ts('start').toUpperCase())) {
                return ts('start')
            }
            if (obj.label.toUpperCase().includes(ts('finish').toUpperCase())) {
                return ts('finish')
            }
            return obj.label || ts('objectRaceLine')
        default:
            return obj.label || obj.type
    }
}

function editObject(obj: EventCourseObject, forceSecond: boolean = false) {
    if (
        !(obj.type === CourseObjectType.Mark || obj.type === CourseObjectType.Gate || obj.type === CourseObjectType.Text
            || obj.type === CourseObjectType.Boat || obj.type === CourseObjectType.RaceLine)
    ) return

    const editLabel = (currentLabel: string, isSecond: boolean = false) => {
        let title = ts('editLabelText')
        if (obj.type === CourseObjectType.Mark) title = ts('editLabelMark')
        if (obj.type === CourseObjectType.Gate) title = ts('editLabelGate')
        if (obj.type === CourseObjectType.Boat) title = ts('editLabelBoat')
        if (obj.type === CourseObjectType.RaceLine) {
            title = isSecond
                ? ts('editLabelRaceLineSecond', { label: currentLabel || ts('line') })
                : ts('editLabelRaceLine', { label: currentLabel || ts('line') })
        }

        quasar.dialog({
            title,
            prompt: {
                model: currentLabel,
                type: 'text',
            },
            cancel: true,
            persistent: true,
        }).onOk(data => {
            const newLabel = String(data).trim()
            if (newLabel === currentLabel) return

            // Validation for Mark and Gate
            if (obj.type === CourseObjectType.Mark || obj.type === CourseObjectType.Gate) {
                const collides = objects.value.some(item => {
                    if (item.id === obj.id) return false
                    if (item.type !== CourseObjectType.Mark && item.type !== CourseObjectType.Gate) return false

                    const existingLabel = item.label

                    // Direct collision
                    if (existingLabel === newLabel) return true

                    // Collision between Mark "2a" and Gate "2"
                    if (obj.type === CourseObjectType.Mark && item.type === CourseObjectType.Gate) {
                        if (newLabel === existingLabel + 'a' || newLabel === existingLabel + 'b') return true
                    }
                    if (obj.type === CourseObjectType.Gate && item.type === CourseObjectType.Mark) {
                        if (existingLabel === newLabel + 'a' || existingLabel === newLabel + 'b') return true
                    }

                    return false
                })

                if (collides) {
                    quasar.notify({
                        type: 'negative',
                        message: ts('labelAlreadyExists'),
                    })
                    return
                }
            }

            const savePayload: Partial<EventCourseObject> = { id: obj.id }
            if (isSecond) savePayload.secondLabel = newLabel
            else savePayload.label = newLabel

            objectsRepo.save(savePayload)

            const event = useRepo(EventCourseModel).with('event').find(course.value?.id || '')?.event
            if (event) {
                event.setModified()
            }
            draw()
        })
    }

    if (forceSecond) {
        editLabel(obj.secondLabel || '', true)
    } else {
        editLabel(obj.label || '', false)
    }
}

function handleDoubleClick(event: MouseEvent) {
    const canvas = canvasRef.value
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = (event.clientX - rect.left) / zoom.value
    const y = (event.clientY - rect.top) / zoom.value

    // Find object under cursor
    const reversedObjects = [...objects.value].reverse()
    let isSecondLabel = false
    const found = reversedObjects.find(obj => {
        // Reuse collision logic from handleMouseDown but without dragging context
        if (obj.type === CourseObjectType.Gate || obj.type === CourseObjectType.RaceLine) {
            const dx2 = x - obj.x2
            const dy2 = y - obj.y2
            if (Math.sqrt(dx2 * dx2 + dy2 * dy2) < 25) {
                isSecondLabel = false
                return true
            }

            const dx1 = x - obj.x
            const dy1 = y - obj.y
            const dist1 = Math.sqrt(dx1 * dx1 + dy1 * dy1)
            let leftTolerance = 25
            if (obj.type === CourseObjectType.RaceLine) leftTolerance = 40

            if (dist1 < leftTolerance) {
                isSecondLabel = false
                return true
            }

            // In non-shared mode, check second line branch (x3, y3)
            if (obj.type === CourseObjectType.RaceLine && obj.secondRaceline && !obj.sharedRaceline) {
                const dx3 = x - obj.x3
                const dy3 = y - obj.y3
                if (Math.sqrt(dx3 * dx3 + dy3 * dy3) < 25) {
                    isSecondLabel = true
                    return true
                }

                const midX2 = (obj.x + obj.x3) / 2
                const midY2 = (obj.y + obj.y3) / 2
                const dmidx2 = x - midX2
                const dmidy2 = y - midY2
                if (Math.sqrt(dmidx2 * dmidx2 + dmidy2 * dmidy2) < 20) {
                    isSecondLabel = true
                    return true
                }
            }

            const midX = (obj.x + obj.x2) / 2
            const midY = (obj.y + obj.y2) / 2
            const dmidx1 = x - midX
            const dmidy1 = y - midY
            if (Math.sqrt(dmidx1 * dmidx1 + dmidy1 * dmidy1) < 20) {
                isSecondLabel = false
                return true
            }

            return false
        }

        const dx = x - obj.x
        const dy = y - obj.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        let tolerance = 25
        if (obj.type === CourseObjectType.Text) tolerance = 40
        if (obj.type === CourseObjectType.Boat) tolerance = 40

        return dist < tolerance
    })

    if (found) {
        editObject(found, isSecondLabel)
    }
}

function addObjectAt(type: CourseObjectType) {
    addObject(type, contextMenuPosition.value.x, contextMenuPosition.value.y)
    contextMenuVisible.value = false
}

function handleContextMenu(event: MouseEvent) {
    const canvas = canvasRef.value
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = (event.clientX - rect.left) / zoom.value
    const y = (event.clientY - rect.top) / zoom.value

    // Find object under cursor
    const reversedObjects = [...objects.value].reverse()
    const found = reversedObjects.find(obj => {
        if (obj.type === CourseObjectType.Gate || obj.type === CourseObjectType.RaceLine) {
            const dx2 = x - obj.x2
            const dy2 = y - obj.y2
            if (Math.sqrt(dx2 * dx2 + dy2 * dy2) < 25) return true

            const dx1 = x - obj.x
            const dy1 = y - obj.y
            let leftTolerance = 25
            if (obj.type === CourseObjectType.RaceLine) leftTolerance = 40

            if (Math.sqrt(dx1 * dx1 + dy1 * dy1) < leftTolerance) return true

            const midX = (obj.x + obj.x2) / 2
            const midY = (obj.y + obj.y2) / 2
            const dx3 = x - midX
            const dy3 = y - midY
            if (Math.sqrt(dx3 * dx3 + dy3 * dy3) < 20) return true

            return false
        }

        const dx = x - obj.x
        const dy = y - obj.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        let tolerance = 25
        if (obj.type === CourseObjectType.Text) tolerance = 40
        if (obj.type === CourseObjectType.Boat) tolerance = 40
        if (obj.type === CourseObjectType.Wind) {
            const dxW = x - obj.x
            const dyW = y - obj.y
            const distW = Math.sqrt(dxW * dxW + dyW * dyW)

            // Area covering arrow and text (approximate)
            return (distW < 140 && dxW < 20 && dxW > -60 && dyW > -10 && dyW < 130)
        }

        return dist < tolerance
    })

    if (found) {
        contextMenuObject.value = found
        contextMenuVisible.value = true
    } else {
        contextMenuObject.value = null
        contextMenuPosition.value = { x, y }
        contextMenuVisible.value = true
        selectedObjectId.value = null
    }
}

onMounted(async () => {
    if (canvasRef.value) {
        const resize = () => {
            // Do not call draw() directly as it might trigger recursive updates via updateCanvasSize
            // Instead, just ensure canvas size is set based on current state
            const canvas = canvasRef.value
            const container = canvas?.parentElement
            if (canvas && container) {
                if (
                    objects.value.length === 0
                    || (objects.value.length === 1 && objects.value[0].type === CourseObjectType.Wind)
                ) {
                    canvasWidth.value = container.clientWidth
                    canvasHeight.value = container.clientHeight
                }
            }
            draw()
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedObjectId.value) {
                // Check if we're in an input or something that should handle the key
                if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
                    return
                }

                const obj = objects.value.find(o => o.id === selectedObjectId.value)
                if (obj && obj.type !== CourseObjectType.Wind) {
                    removeObject(obj)
                    selectedObjectId.value = null
                }
            }
        }

        window.addEventListener('resize', resize)
        window.addEventListener('keydown', handleKeyDown)

        onUnmounted(() => {
            window.removeEventListener('resize', resize)
            window.removeEventListener('keydown', handleKeyDown)
        })

        // Force font load before first draw to avoid Times New Roman flash
        if ('fonts' in document) {
            document.fonts.load('12px Roboto').then(() => {
                draw()
            })
        }

        resize()
    }

    // Ensure exactly one wind object exists
    const winds = objectsRepo.where('eventCourseId', course.value.id).where('type', CourseObjectType.Wind).get()
    if (winds.length === 0) {
        // Wait a bit to ensure the canvas has its real size
        await nextTick()

        const canvas = canvasRef.value
        const x = canvas ? (canvas.parentElement ? canvas.parentElement.clientWidth * 0.75 : canvas.width * 0.75) : 600
        const y = 100

        objectsRepo.save({
            eventCourseId: course.value.id,
            eventId: props.eventId,
            type: CourseObjectType.Wind,
            x,
            y,
            label: '',
        })
    } else if (winds.length > 1) {
        // Keep only the first one, delete others
        const toDelete = winds.slice(1).map(w => w.id)
        objectsRepo.destroy(toDelete)
    }
})

watch(course, async (newCourse) => {
    if (!newCourse) return

    const winds = objectsRepo.where('eventCourseId', newCourse.id).where('type', CourseObjectType.Wind).get()
    if (winds.length === 0) {
        await nextTick()
        const canvas = canvasRef.value
        const x = canvas ? (canvas.parentElement ? canvas.parentElement.clientWidth * 0.75 : canvas.width * 0.75) : 600
        const y = 100

        objectsRepo.save({
            eventCourseId: newCourse.id,
            eventId: props.eventId,
            type: CourseObjectType.Wind,
            x,
            y,
            label: '',
        })
    } else if (winds.length > 1) {
        const toDelete = winds.slice(1).map(w => w.id)
        objectsRepo.destroy(toDelete)
    }
}, { immediate: true })

watch(objects, () => {
    draw()
}, { deep: true })
</script>

<style lang="sass" scoped>
.track-editor
    display: flex
    flex-direction: column
    width: 100%
    height: 100%
    min-height: 500px
    background-color: white

.track-editor-toolbar
    border-bottom: 1px solid #ccc
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05)
    z-index: 10

.track-editor-canvas-container
    position: relative
    background-color: #eee

.track-editor-canvas
    display: block
    background-color: #fafafa

.color-picker-box
    width: 24px
    height: 24px
    border: 1px solid #ccc
    border-radius: 4px
    transition: transform 0.2s

    &:hover
        transform: scale(1.2)
        border-color: #999

.mark-icon-container
    display: flex
    align-items: center
    justify-content: center
    width: 24px
    height: 24px

.q-item__section--avatar
    min-width: 32px
    padding-right: 8px
</style>
